import React, { Suspense, lazy, useState, useEffect, useRef, createContext, useContext } from "react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import { getProfessorPobreExplanation } from "./services/aiService";
import {
  fetchAiCommentFromDB,
  saveAiCommentToDB,
} from "./services/aiCommentService";
import { saveExplanation } from "./services/savedExplanationService";
import OnboardingWizard, {
  OnboardingData,
} from "./components/OnboardingWizard";
import StudyPlanDisplay from "./components/StudyPlanDisplay";
import AuthModal from "./components/AuthModal";
import FilterPanel from "./components/FilterPanel";
import Pagination from "./components/Pagination";
import QuestionSidebar from "./components/QuestionSidebar";
import NewLandingPage from "./LandingPage";
const AdminPage = lazy(() => import("./pages/AdminPage"));
import CheckoutPage from "./components/CheckoutPage";
import SuccessPage from "./components/SuccessPage";
import GuidesPage from "./pages/GuidesPage";
import SimuladosPage from "./components/SimuladosPage";
import ApprovalMapPage from "./components/ApprovalMapPage";
import MaterialsPage from "./components/MaterialsPage";
import FlashcardsPage from "./components/FlashcardsPage";
import AulasPage from "./components/AulasPage";
import SavedExplanationsPage from "./components/SavedExplanationsPage";
import { TUTORIALS } from "./data/tutorials";
import { auth, db } from "./services/firebase";
import { onAuthStateChanged, User } from "firebase/auth";
import { collection, getDocs, orderBy, query, limit as fbLimit } from "firebase/firestore";
import {
  getUserSubscription,
  saveUserAnswer,
  getGamificationData,
  canAnswerQuestion,
  toggleBookmark,
  getUserProgress,
  updateUserProfile,
  getBadgeDefinitions,
} from "./services/userProgressService";
import {
  getQuestionsCount,
  getQuestionsPage,
  getFilterOptions,
  Question,
  QuestionFilters,
  FilterOptions,
} from "./services/questionService";
import UserStats from "./components/UserStats";
import {
  getLeaderboard,
  LeaderboardEntry,
  getUserLeaderboardEntry,
} from "./services/leaderboardService";
import {
  saveOnboardingProgress,
  getOnboardingProgress,
  completeOnboarding,
  hasCompletedOnboarding,
  saveStudyPlan,
  getStudyPlan,
  getUserEntitlements,
  incrementQuestionsUsed,
  resetOnboarding,
  incrementAIExplanationsUsed,
  UserEntitlements,
} from "./services/onboardingService";
import { getTenantBySlug, isTenantMember } from "./services/tenantService";

// Helper para resolver matéria/tópico a partir do plano/filtros, evitando ReferenceError
function resolveFiltersFromPlanHelper(
  filterOptions: FilterOptions,
  subject?: string,
  topic?: string,
): QuestionFilters {
  const normalizeLabel = (text: string = "") =>
    text
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .toLowerCase()
      .trim();

  const normSubject = normalizeLabel(subject || "");
  const normTopic = normalizeLabel(topic || "");

  const scoreMatch = (candidate: string, target: string) => {
    const nc = normalizeLabel(candidate);
    if (!nc || !target) return 0;
    if (nc === target) return 100;
    if (nc.startsWith(target) || target.startsWith(nc)) return 80;
    if (nc.includes(target) || target.includes(nc)) return 60;
    return 0;
  };

  let bestSubject: string | undefined;
  let bestSubjectScore = 0;
  for (const s of filterOptions.subjects || []) {
    const score = scoreMatch(s, normSubject);
    if (score > bestSubjectScore) {
      bestSubject = s;
      bestSubjectScore = score;
    }
  }

  let bestTopic: string | undefined;
  let bestTopicScore = 0;
  const topicSource = bestSubject && filterOptions.topics?.[bestSubject]
    ? filterOptions.topics[bestSubject]
    : Object.values(filterOptions.topics || {}).flat();

  for (const t of topicSource) {
    const score = scoreMatch(t, normTopic);
    if (score > bestTopicScore) {
      bestTopic = t;
      bestTopicScore = score;
    }
  }

  const topicThreshold = 70;
  return {
    subject: bestSubject || subject,
    topic: bestTopicScore >= topicThreshold ? bestTopic : undefined,
  } as QuestionFilters;
}

// ==================== CONSTANTS ====================
const QUESTIONS_PER_PAGE = 10;

// ==================== CONTEXT & UTILITIES ====================

interface Toast {
  id: string;
  type: "success" | "error" | "info" | "warning";
  message: string;
}

interface Badge {
  id: string;
  name: string;
  description: string;
  icon: string;
  unlockedAt?: any;
}

interface AppContextType {
  toasts: Toast[];
  showToast: (type: Toast["type"], message: string) => void;
  removeToast: (id: string) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

const useApp = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error("useApp must be used within AppProvider");
  return context;
};

const normalizeLabel = (text: string = "") =>
  text
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

const LazyFallback: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-white/80 backdrop-blur-sm">
    <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full"></div>
  </div>
);

const ToastContainer: React.FC = () => {
  const { toasts, removeToast } = useApp();

  const getStyles = (type: Toast["type"]) => {
    switch (type) {
      case "success":
        return "bg-emerald-500";
      case "error":
        return "bg-red-500";
      case "warning":
        return "bg-amber-500";
      default:
        return "bg-blue-500";
    }
  };

  const getIcon = (type: Toast["type"]) => {
    switch (type) {
      case "success":
        return "✓";
      case "error":
        return "✕";
      case "warning":
        return "⚠";
      default:
        return "ℹ";
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-[200] flex flex-col gap-2">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`${getStyles(toast.type)} text-white px-4 py-3 rounded-xl shadow-lg flex items-center gap-3 min-w-[280px] animate-slide-in`}
        >
          <span className="text-lg">{getIcon(toast.type)}</span>
          <span className="flex-1 font-medium text-sm">{toast.message}</span>
          <button
            onClick={() => removeToast(toast.id)}
            className="opacity-70 hover:opacity-100"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
};

const Confetti: React.FC<{ show: boolean }> = ({ show }) => {
  const [particles, setParticles] = useState<
    Array<{ id: number; x: number; color: string; delay: number }>
  >([]);

  useEffect(() => {
    if (show) {
      const newParticles = Array.from({ length: 50 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        color: [
          "#10b981",
          "#3b82f6",
          "#f59e0b",
          "#ef4444",
          "#8b5cf6",
          "#ec4899",
        ][Math.floor(Math.random() * 6)],
        delay: Math.random() * 0.5,
      }));
      setParticles(newParticles);
      setTimeout(() => setParticles([]), 2500);
    }
  }, [show]);

  if (!show || particles.length === 0) return null;

  return (
    <div className="fixed inset-0 pointer-events-none z-[150] overflow-hidden">
      {particles.map((p) => (
        <div
          key={p.id}
          className="absolute w-3 h-3 rounded-sm animate-confetti"
          style={{
            left: `${p.x}%`,
            backgroundColor: p.color,
            animationDelay: `${p.delay}s`,
          }}
        />
      ))}
    </div>
  );
};

const LimitPopup: React.FC<{
  onSubscribe: () => void;
  onClose: () => void;
  remaining: number;
}> = ({ onSubscribe, onClose, remaining }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
    <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl animate-fade-in">
      <div className="text-center">
        <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <span className="text-4xl">🔒</span>
        </div>
        <h2 className="text-2xl font-black text-gray-900 mb-3">
          Limite Atingido!
        </h2>
        <p className="text-gray-600 mb-6">
          Você usou todas as suas questões gratuitas de hoje. Assine o plano
          NOMEADO para continuar estudando sem limites!
        </p>
        <div className="space-y-3">
          <button
            onClick={onSubscribe}
            className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-4 rounded-2xl transition shadow-lg shadow-emerald-500/20"
          >
            Assinar NOMEADO - R$ 9,90 no 1º mês
          </button>
          <button
            onClick={onClose}
            className="w-full text-gray-500 hover:text-gray-700 font-semibold py-2 transition"
          >
            Voltar (amanhã você terá mais {remaining} questões)
          </button>
        </div>
      </div>
    </div>
  </div>
);

// ==================== TYPES ====================

interface UserProfile {
  nickname: string;
  displayName: string;
  photoURL: string;
  targetExam: string;
  studyGoal: number;
}

const sampleQuestions: Question[] = [
  {
    id: "1",
    subject: "Direito Constitucional",
    topic: "Direitos Fundamentais",
    year: 2024,
    banca: "FGV",
    institution: "TJ-RJ",
    text: "De acordo com a Constituição Federal de 1988, são direitos sociais, EXCETO:",
    options: [
      { id: "a", text: "A educação" },
      { id: "b", text: "A saúde" },
      { id: "c", text: "A propriedade" },
      { id: "d", text: "O trabalho" },
    ],
    correctOptionId: "c",
    comments:
      "A propriedade é um direito individual, não social. Os direitos sociais estão previstos no art. 6º da CF/88.",
  },
  {
    id: "2",
    subject: "Direito Administrativo",
    topic: "Atos Administrativos",
    year: 2023,
    banca: "FGV",
    institution: "TJ-RJ",
    text: "São atributos do ato administrativo, EXCETO:",
    options: [
      { id: "a", text: "Presunção de legitimidade" },
      { id: "b", text: "Imperatividade" },
      { id: "c", text: "Autoexecutoriedade" },
      { id: "d", text: "Economicidade" },
    ],
    correctOptionId: "d",
    comments:
      "Economicidade é um princípio da administração pública, não um atributo do ato administrativo.",
  },
  {
    id: "3",
    subject: "Língua Portuguesa",
    topic: "Concordância Verbal",
    year: 2024,
    banca: "FGV",
    institution: "TJ-RJ",
    text: "Assinale a alternativa em que a concordância verbal está INCORRETA:",
    options: [
      { id: "a", text: "Fazem dez anos que não o vejo." },
      { id: "b", text: "Houve muitos problemas na reunião." },
      { id: "c", text: "Existem várias soluções para o caso." },
      { id: "d", text: "Devem haver mais de cem pessoas aqui." },
    ],
    correctOptionId: "a",
    comments:
      'O verbo "fazer" indicando tempo é impessoal, devendo ficar no singular: "Faz dez anos".',
  },
  {
    id: "4",
    subject: "Raciocínio Lógico",
    topic: "Proposições",
    year: 2023,
    banca: "CESPE",
    institution: "INSS",
    text: 'A negação da proposição "Todos os servidores são eficientes" é:',
    options: [
      { id: "a", text: "Nenhum servidor é eficiente" },
      { id: "b", text: "Algum servidor não é eficiente" },
      { id: "c", text: "Todos os servidores são ineficientes" },
      { id: "d", text: "Alguns servidores são eficientes" },
    ],
    correctOptionId: "b",
    comments:
      'A negação de "Todo A é B" é "Algum A não é B" (ou "Existe A que não é B").',
  },
  {
    id: "5",
    subject: "Direito Constitucional",
    topic: "Organização do Estado",
    year: 2024,
    banca: "FGV",
    institution: "TJ-RJ",
    text: "A República Federativa do Brasil tem como fundamentos, EXCETO:",
    options: [
      { id: "a", text: "A soberania" },
      { id: "b", text: "A cidadania" },
      { id: "c", text: "A dignidade da pessoa humana" },
      { id: "d", text: "A erradicação da pobreza" },
    ],
    correctOptionId: "d",
    comments:
      "A erradicação da pobreza é um objetivo fundamental (Art. 3º), não um fundamento (Art. 1º).",
  },
];

// ==================== PROFILE PAGE ====================

const ProfilePage: React.FC<{
  user: User | null;
  onBack: () => void;
  userProfile: UserProfile;
  onSave: (profile: UserProfile) => void;
  showToast: (type: Toast["type"], message: string) => void;
  onboardingData: OnboardingData | null;
}> = ({ onBack, userProfile, onSave, showToast, onboardingData }) => {
  const [profile, setProfile] = useState(userProfile);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    onSave(profile);
    showToast("success", "Perfil atualizado com sucesso!");
    setSaving(false);
    onBack();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-100 px-4 py-4">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <h1 className="text-2xl font-black text-gray-900">Meu Perfil</h1>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-[2.5rem] p-8 shadow-sm border border-gray-100 space-y-8">
          {/* Hero do Perfil */}
          <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-3xl p-6 text-white flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center overflow-hidden">
                {profile.photoURL ? (
                  <img
                    src={profile.photoURL}
                    alt="Avatar"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-2xl font-black">
                    {(profile.nickname ||
                      profile.displayName ||
                      "U")[0]?.toUpperCase()}
                  </span>
                )}
              </div>
              <div>
                <h2 className="text-2xl font-black">
                  {profile.nickname || "Seu Perfil"}
                </h2>
                <p className="text-emerald-50 text-sm">
                  {profile.displayName ||
                    "Complete seus dados para personalizar a experiência"}
                </p>
              </div>
            </div>
            <div className="bg-white/15 rounded-2xl px-4 py-3 text-sm">
              <p className="text-emerald-50">Concurso alvo</p>
              <p className="font-bold">
                {profile.targetExam ||
                  onboardingData?.targetExam ||
                  "Definir concurso"}
              </p>
            </div>
          </div>

          {/* Formulário */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Apelido
              </label>
              <input
                type="text"
                value={profile.nickname}
                onChange={(e) =>
                  setProfile({ ...profile, nickname: e.target.value })
                }
                className="w-full bg-gray-50 border border-gray-200 rounded-2xl px-4 py-3 text-gray-900 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Nome Completo
              </label>
              <input
                type="text"
                value={profile.displayName}
                onChange={(e) =>
                  setProfile({ ...profile, displayName: e.target.value })
                }
                className="w-full bg-gray-50 border border-gray-200 rounded-2xl px-4 py-3 text-gray-900 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Concurso Alvo
              </label>
              <input
                type="text"
                value={profile.targetExam || onboardingData?.targetExam || ""}
                onChange={(e) =>
                  setProfile({ ...profile, targetExam: e.target.value })
                }
                className="w-full bg-gray-50 border border-gray-200 rounded-2xl px-4 py-3 text-gray-900 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Meta Diária (questões)
              </label>
              <div className="flex items-center gap-4">
                <input
                  type="range"
                  min={5}
                  max={100}
                  value={profile.studyGoal}
                  onChange={(e) =>
                    setProfile({
                      ...profile,
                      studyGoal: parseInt(e.target.value) || 0,
                    })
                  }
                  className="w-full accent-emerald-500"
                />
                <div className="min-w-[64px] text-center bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 font-bold text-gray-900">
                  {profile.studyGoal}
                </div>
              </div>
            </div>
          </div>

          {/* Dados do Onboarding (somente leitura) */}
          {onboardingData && (
            <div className="pt-2">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                Diagnóstico
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div className="p-4 bg-gray-50 rounded-2xl">
                  <p className="text-gray-500">Horas/dia</p>
                  <p className="font-bold text-gray-900">
                    {onboardingData.hoursPerDay}h
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-2xl">
                  <p className="text-gray-500">Dias/semana</p>
                  <p className="font-bold text-gray-900">
                    {onboardingData.daysPerWeek} dias
                  </p>
                </div>
                {onboardingData.targetBanca && (
                  <div className="p-4 bg-gray-50 rounded-2xl">
                    <p className="text-gray-500">Banca</p>
                    <p className="font-bold text-gray-900">
                      {onboardingData.targetBanca}
                    </p>
                  </div>
                )}
                {onboardingData.targetArea && (
                  <div className="p-4 bg-gray-50 rounded-2xl">
                    <p className="text-gray-500">Área</p>
                    <p className="font-bold text-gray-900">
                      {onboardingData.targetArea}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-300 text-white font-bold py-4 rounded-2xl transition"
          >
            {saving ? "Salvando..." : "Salvar Alterações"}
          </button>
        </div>
      </main>
    </div>
  );
};

// ==================== DASHBOARD ====================

const Dashboard: React.FC<{
  user: User | null;
  onLogout: () => void;
  entitlements: UserEntitlements;
  onSubscribe: () => void;
  showToast: (type: Toast["type"], message: string) => void;
  userProfile: UserProfile;
  onEditProfile: () => void;
  onViewPerformance: () => void;
  onViewSimulados: () => void; // Nova prop
  onViewRanking: () => void;
  onViewMaterials: () => void;
  onboardingData: OnboardingData | null;
  initialFilters?: QuestionFilters;
  onViewStudyPlan: () => void;
  onViewApprovalMap: () => void;
  onGoHome: () => void;
  onViewFlashcards: (filters: QuestionFilters) => void;
  onViewSavedExplanations: () => void;
  onResetPlan: () => void; // Nova prop para refazer plano
  planExpiresAt: Date | null;
  onAnswerSubmit: (
    questionId: string | number,
    selectedOptionId: string,
    isCorrect: boolean,
    timeSpentSeconds: number,
    topic: string,
    difficulty: string,
    examId: string,
  ) => Promise<string[] | undefined>;
}> = ({
  user,
  onLogout,
  entitlements,
  onSubscribe,
  showToast,
  userProfile,
  onEditProfile,
  onViewPerformance,
  onViewSimulados,
  onViewRanking,
  onViewMaterials,
  onboardingData,
  initialFilters,
  onViewStudyPlan,
  onViewApprovalMap,
  onGoHome,
  onViewFlashcards,
  onViewSavedExplanations,
  onResetPlan,
  planExpiresAt,
}) => {
  const [showResetPlanModal, setShowResetPlanModal] = useState(false); // Modal de confirmação
  const [questions, setQuestions] = useState<Question[]>(sampleQuestions);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [explanation, setExplanation] = useState<string>("");
  const [loadingExplanation, setLoadingExplanation] = useState(false);
  const [showLimitPopup, setShowLimitPopup] = useState(false);
  const [showSimuladosUpgrade, setShowSimuladosUpgrade] = useState(false);
  const [showAiLimitPopup, setShowAiLimitPopup] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [streak, setStreak] = useState(0);
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);
  const [totalAnswered, setTotalAnswered] = useState(0);
  const [totalCorrect, setTotalCorrect] = useState(0);
  const [showSidebar, setShowSidebar] = useState(false);
  const [answeredQuestions, setAnsweredQuestions] = useState<{
    [key: string]: boolean;
  }>({});
  const [questionsUsedToday, setQuestionsUsedToday] = useState(
    entitlements.dailyQuestionsUsed,
  );
  const [bookmarkedQuestions, setBookmarkedQuestions] = useState<string[]>([]);
  const [fontSize, setFontSize] = useState<"small" | "medium" | "large">(
    "medium",
  );
  const [focusMode, setFocusMode] = useState(false);
  const [highlightMode, setHighlightMode] = useState(false);
  const [savingExplanation, setSavingExplanation] = useState(false);
  const [saveFeedback, setSaveFeedback] = useState<string | null>(null);

  const DEFAULT_FILTERS: QuestionFilters = {
    subject: "Todas",
    topic: "Todos",
    banca: "Todas",
    year: "Todos",
    institution: "Todos",
    role: "Todos",
    status: "Todas",
    keyword: "",
  };
  const LAST_FILTERS_KEY = "cp:lastFilters";

  const statusLabels: Record<string, string> = {
    answered: "Respondidas",
    unanswered: "Não respondidas",
    correct: "Acertei",
    incorrect: "Errei",
  };

  const normalizeFilters = (value?: QuestionFilters) => ({
    ...DEFAULT_FILTERS,
    ...(value || {}),
  });

  const hasCustomFilters = (value?: QuestionFilters) => {
    const normalized = normalizeFilters(value);
    return (
      normalized.subject !== "Todas" ||
      normalized.topic !== "Todos" ||
      normalized.banca !== "Todas" ||
      normalized.year !== "Todos" ||
      normalized.institution !== "Todos" ||
      normalized.role !== "Todos" ||
      normalized.status !== "Todas" ||
      Boolean(normalized.keyword?.trim())
    );
  };

  // Pagination and filters
  const [currentPage, setCurrentPage] = useState(1);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [pageCursors, setPageCursors] = useState<{ [page: number]: any }>({});
  const [filters, setFilters] = useState<QuestionFilters>(() =>
    normalizeFilters(initialFilters),
  );
  const [lastFilterReminder, setLastFilterReminder] =
    useState<QuestionFilters | null>(null);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    subjects: [],
    topics: {},
    bancas: [],
    institutions: [],
    years: [],
    roles: [],
  });
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(true);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [purchasedItems, setPurchasedItems] = useState<string[]>([]);

  console.log("App Version: 1.2 - Fix purchasedItems");

  const remainingQuestions =
    entitlements.dailyQuestionsLimit - questionsUsedToday;
  const canAnswer = entitlements.plan !== "free" || remainingQuestions > 0;

  useEffect(() => {
    if (!hasStarted) return;
    if (typeof window !== "undefined" && window.innerWidth < 1024) {
      setFiltersCollapsed(true); // mobile/tablet: recolhe para ganhar espaço
    } else {
      setFiltersCollapsed(false); // desktop: mantém filtros visíveis
    }
  }, [hasStarted]);

  const handleSimuladosClick = () => {
    if (entitlements.plan === "free") {
      setShowSimuladosUpgrade(true);
      return;
    }
    onViewSimulados();
  };

  // Recupera último filtro usado (estado inicial)
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const stored = localStorage.getItem(LAST_FILTERS_KEY);
      if (stored) {
        const parsed = normalizeFilters(JSON.parse(stored));
        setLastFilterReminder(parsed);
        if (!initialFilters || Object.keys(initialFilters).length === 0) {
          setFilters(parsed);
        }
      }
    } catch (error) {
      console.warn("Não foi possível carregar último filtro salvo", error);
    }
  }, [initialFilters]);

  // Aplica filtros que chegam de outras telas (Simulados/Plano)
  useEffect(() => {
    if (initialFilters && Object.keys(initialFilters).length > 0) {
      const merged = normalizeFilters(initialFilters);
      setFilters(merged);
      setLastFilterReminder(merged);
      setHasStarted(true);
      setCurrentPage(1);
      setPageCursors({});
    }
  }, [initialFilters]);

  // Persiste filtros para lembrar na próxima visita
  useEffect(() => {
    const normalized = normalizeFilters(filters);
    setLastFilterReminder(normalized);
    if (typeof window === "undefined") return;
    try {
      localStorage.setItem(LAST_FILTERS_KEY, JSON.stringify(normalized));
    } catch (error) {
      console.warn("Não foi possível salvar filtros localmente", error);
    }
  }, [filters]);

  // Load filter options on mount
  useEffect(() => {
    const loadFilterOptions = async () => {
      setFilterOptionsLoading(true);
      try {
        const options = await getFilterOptions();
        setFilterOptions(options);
      } catch (error) {
        console.error("Error loading filter options:", error);
      } finally {
        setFilterOptionsLoading(false);
      }
    };
    loadFilterOptions();
  }, []);

  // Atualiza contagem de questões mesmo antes de começar
  useEffect(() => {
    let cancelled = false;
    const updateCount = async () => {
      try {
        const count = await getQuestionsCount(filters);
        if (cancelled) return;
        if (count >= 0) {
          setTotalQuestions(count);
        }
      } catch (error) {
        console.error("Erro ao contar questões antes de começar:", error);
      }
    };

    if (!hasStarted) {
      updateCount();
    }

    return () => {
      cancelled = true;
    };
  }, [filters, hasStarted]);

  // Load questions and gamification data when filters change (resetting page) or when page changes
  useEffect(() => {
    const loadQuestions = async () => {
      if (!hasStarted) {
        setLoadingQuestions(false);
        setQuestions([]);
        return;
      }
      setLoadingQuestions(true);
      try {
        // 1. Fetch gamification data (for history/answered status)
        const gamification = await getGamificationData();
        let loadedAnsweredQuestions: { [key: string]: boolean } = {};

        if (gamification) {
          setStreak(gamification.currentStreak);
          setTotalAnswered(gamification.totalAnswered);
          setTotalCorrect(gamification.totalCorrect);
          if (gamification.bookmarks) {
            setBookmarkedQuestions(gamification.bookmarks);
          }

          // Populate answered map from history
          if (gamification.history) {
            loadedAnsweredQuestions = gamification.history.reduce(
              (acc: any, curr: any) => {
                // Ensure we use string ID
                acc[String(curr.questionId)] = curr.isCorrect;
                return acc;
              },
              {},
            );
            setAnsweredQuestions((prev) => ({
              ...prev,
              ...loadedAnsweredQuestions,
            }));
          }
        }

        // 2. Paginação server-side (sem carregar o banco inteiro)
        let cursor = null;
        if (currentPage > 1) {
          if (pageCursors[currentPage - 1]) {
            cursor = pageCursors[currentPage - 1];
          } else {
            let tempCursor: any = null;
            for (let p = 1; p < currentPage; p++) {
              const res = await getQuestionsPage(
                QUESTIONS_PER_PAGE,
                tempCursor,
                filters,
              );
              tempCursor = res.cursor;
              setPageCursors((prev) => ({ ...prev, [p]: tempCursor }));
              if (!res.hasMore) break;
            }
            cursor = tempCursor;
          }
        }

        const pageResult = await getQuestionsPage(
          QUESTIONS_PER_PAGE,
          cursor,
          filters,
        );
        setPageCursors((prev) => ({
          ...prev,
          [currentPage]: pageResult.cursor,
        }));

        // 3. Filtros client-side complementares (keyword)
        let filtered = pageResult.questions;
        if (filters.keyword) {
          const lowerKw = filters.keyword.toLowerCase();
          filtered = filtered.filter((q) =>
            q.text.toLowerCase().includes(lowerKw),
          );
        }

        // 4. Update totals (sem depender de 10k)
        const totalCount = pageResult.totalCount;
        setTotalQuestions(
          totalCount >= 0
            ? totalCount
            : (currentPage - 1) * QUESTIONS_PER_PAGE + filtered.length,
        );

        // 5. Set page questions
        setQuestions(filtered);
        if (filtered.length > 0) {
          setCurrentQuestionIndex(0);
        }
      } catch (error) {
        console.error("Error loading questions:", error);
        setQuestions(sampleQuestions);
        setTotalQuestions(sampleQuestions.length);
      } finally {
        setLoadingQuestions(false);
      }
    };
    loadQuestions();
  }, [currentPage, filters, hasStarted]);

  const handleBookmark = async (questionId: string) => {
    try {
      const newState = await toggleBookmark(questionId);
      if (newState) {
        setBookmarkedQuestions((prev) => [...prev, questionId]);
        showToast("success", "Questão salva nos favoritos!");
      } else {
        setBookmarkedQuestions((prev) =>
          prev.filter((id) => id !== questionId),
        );
        showToast("info", "Questão removida dos favoritos.");
      }
    } catch (error) {
      console.error("Erro ao favoritar:", error);
      showToast("error", "Erro ao salvar favorito.");
    }
  };

  // UX Helpers
  const getFontSizeClass = (type: "question" | "option") => {
    switch (fontSize) {
      case "small":
        return type === "question" ? "text-base" : "text-sm";
      case "large":
        return type === "question" ? "text-2xl leading-loose" : "text-lg";
      default:
        return type === "question" ? "text-lg leading-relaxed" : "text-base";
    }
  };

  const currentQuestion = questions[currentQuestionIndex];

  const handleSaveExplanation = async () => {
    if (!currentQuestion || !explanation) return;
    setSavingExplanation(true);
    setSaveFeedback(null);
    try {
      await saveExplanation({
        questionId: String(currentQuestion.id ?? ""),
        examId: (currentQuestion as any).examId || "",
        subject: currentQuestion.subject || "Geral",
        topic: currentQuestion.topic || "Geral",
        statement: currentQuestion.text || (currentQuestion as any).statement || "",
        explanation,
        source: "question",
        optionChosen: selectedOption || undefined,
      });
      setSaveFeedback("Explicação salva no caderno.");
    } catch (err) {
      console.error("Erro ao salvar explicação", err);
      setSaveFeedback("Erro ao salvar. Tente novamente.");
    } finally {
      setSavingExplanation(false);
    }
  };

  const handleOptionSelect = (optionId: string) => {
    if (showResult || !canAnswer) return;
    setSelectedOption(optionId);
  };

  const handleConfirm = async () => {
    if (!selectedOption || !currentQuestion) return;
    const isCorrect = selectedOption === currentQuestion.correctOptionId;

    // Salva a resposta no banco de dados (userProgressService)
    if (user) {
      try {
        // Correção: Passar todos os parâmetros necessários para saveUserAnswer
        const timeSpent = 0; // Placeholder para tempo gasto
        const newlyUnlockedBadgeIds = await saveUserAnswer(
          currentQuestion.id,
          selectedOption,
          isCorrect,
          timeSpent,
          currentQuestion.topic || "",
          currentQuestion.difficulty || "média",
          currentQuestion.examId || "",
          currentQuestion.subject || "",
        );

        if (newlyUnlockedBadgeIds && newlyUnlockedBadgeIds.length > 0) {
          // Disparar animação/notificação de novo badge
          // Para simplificar, vou apenas mostrar um toast para cada badge
          newlyUnlockedBadgeIds.forEach((badgeId) => {
            const badgeDef =
              getBadgeDefinitions()[
                badgeId as keyof typeof getBadgeDefinitions
              ];
            if (badgeDef) {
              showToast("success", `🏆 Novo Badge: ${badgeDef.name}!`);
            }
          });
        }
      } catch (error) {
        console.error("Error saving answer:", error);
      }
    }

    setShowResult(true);
    setAnsweredQuestions((prev) => ({
      ...prev,
      [currentQuestion.id]: isCorrect,
    }));
    setTotalAnswered((prev) => prev + 1);

    if (isCorrect) {
      setTotalCorrect((prev) => prev + 1);
      setStreak((prev) => prev + 1);
      if (streak >= 2) {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 2500);
      }
    } else {
      setStreak(0);
    }

    // Atualiza o contador de questões usadas hoje
    if (entitlements.plan === "free") {
      try {
        const status = await canAnswerQuestion();
        setQuestionsUsedToday(
          entitlements.dailyQuestionsLimit - status.remaining,
        );
        if (!status.canAnswer) {
          setShowLimitPopup(true);
        }
      } catch (error) {
        console.error("Error updating questions used:", error);
      }
    }
  };

  const handleAskProfessor = async () => {
    if (!selectedOption) return;

    setShowExplanation(true);
    setLoadingExplanation(true);

    try {
      // 1) Tenta cache global por questão
      const cached = await fetchAiCommentFromDB(String(currentQuestion.id));
      if (cached) {
        setExplanation(cached);
        return;
      }

      // 2) Limite de explicações só conta quando precisa chamar a IA
      if (entitlements?.plan === "free" && typeof setEntitlements === "function") {
        const remaining =
          (entitlements.aiExplanationLimit || 0) - (entitlements.aiExplanationsUsed || 0);
        if (remaining <= 0) {
          setShowAiLimitPopup(true);
          return;
        }
        try {
          await incrementAIExplanationsUsed();
          setEntitlements((prev) => ({
            ...prev,
            aiExplanationsUsed: (prev.aiExplanationsUsed || 0) + 1,
          }));
        } catch (err) {
          console.warn("Falha ao incrementar explicações", err);
        }
      }

      const result = await getProfessorPobreExplanation(
        currentQuestion,
        selectedOption,
      );
      setExplanation(result);

      // 3) Salva no cache para próximos usuários
      await saveAiCommentToDB(String(currentQuestion.id), result);
    } catch (error: any) {
      setExplanation(
        `❌ **Erro ao carregar explicação**\n\n${error.message || "Tente novamente mais tarde."}`,
      );
    } finally {
      setLoadingExplanation(false);
    }
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    } else if (currentPage * QUESTIONS_PER_PAGE < totalQuestions) {
      setCurrentPage((prev) => prev + 1);
    }
    setSelectedOption(null);
    setShowResult(false);
    setShowExplanation(false);
    setExplanation("");
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => {
      const newFilters = { ...prev, [key]: value };
      if (key === "subject") {
        newFilters.topic = "Todos";
      }
      return newFilters;
    });
    setCurrentPage(1);
    setPageCursors({});
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const accuracy =
    totalAnswered > 0 ? Math.round((totalCorrect / totalAnswered) * 100) : 0;
  const userInitial = (
    userProfile.nickname ||
    userProfile.displayName ||
    user?.email ||
    "U"
  )
    .charAt(0)
    .toUpperCase();

  const planDaysRemaining = planExpiresAt
    ? Math.max(0, Math.ceil((planExpiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;
  const hasChecklistOnboarding = !!onboardingData;
  const checklistDone = {
    onboarding: hasChecklistOnboarding,
    mapa: hasChecklistOnboarding,
    questoes: totalAnswered > 0,
  };

  // Fallback de anos para o FilterPanel (caso metadata venha vazio)
  const fallbackYearsList = React.useMemo(() => {
    const list: string[] = [];
    for (let y = 2026; y >= 1995; y--) list.push(String(y));
    return list;
  }, []);
  const yearsForFilters = React.useMemo(() => {
    const cleaned = (filterOptions.years || [])
      .map((y) => String(y).trim())
      .filter((y) => y && y !== "Todos");
    return cleaned.length > 0 ? cleaned : fallbackYearsList;
  }, [filterOptions.years, fallbackYearsList]);

  // Se years vier vazio do backend, força fallback no estado para evitar dropdown vazio
  React.useEffect(() => {
    const cleaned = (filterOptions.years || [])
      .map((y) => String(y).trim())
      .filter((y) => y && y !== "Todos");
    if (cleaned.length === 0) {
      setFilterOptions((prev) => ({ ...prev, years: fallbackYearsList }));
    }
  }, [filterOptions.years, fallbackYearsList]);

  return (
    <div className="relative min-h-screen bg-slate-50">
      {/* Botão flutuante de feedback */}
      <button
        onClick={() => setShowFeedbackModal(true)}
        className="fixed bottom-4 right-4 z-50 flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-3 py-2 rounded-full shadow-md shadow-emerald-500/30 transition text-xs sm:text-sm"
        aria-label="Reportar erro"
      >
        <span role="img" aria-hidden="true" className="text-sm sm:text-base">💬</span>
        <span className="hidden sm:inline">Achou um erro?</span>
        <span className="inline sm:hidden">Feedback</span>
      </button>

      {showFeedbackModal && (
        <div className="fixed inset-0 z-[120] bg-black/60 flex items-center justify-center p-2">
          <div className="bg-white rounded-xl shadow-lg max-w-xs w-full p-3 relative">
            <button
              onClick={() => setShowFeedbackModal(false)}
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
              aria-label="Fechar"
            >
              ✕
            </button>
            <div className="flex items-start gap-2">
              <div className="text-lg">💬</div>
              <div className="space-y-1">
                <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-[0.08em]">Feedback</p>
                <h3 className="text-base font-black text-gray-900 leading-tight">Achou um erro?</h3>
                <p className="text-xs text-gray-600">Fala com a gente e ganha 1 mês grátis.</p>
              </div>
            </div>
            <div className="mt-3 space-y-2">
              <a
                href="https://wa.me/5521975064950?text=Oi%2C%20encontrei%20um%20erro%20no%20app%20Concurseiro%20Pobre.%20Pode%20me%20ajudar%3F"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-3 py-2 rounded-lg shadow-md shadow-emerald-500/20 transition text-sm"
              >
                <span className="text-base" aria-hidden="true">🟢</span>
                <span>WhatsApp</span>
              </a>
              <button
                onClick={() => setShowFeedbackModal(false)}
                className="w-full text-gray-500 hover:text-gray-700 font-semibold py-2 text-sm"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* removed radial overlay */}
      <Confetti show={showConfetti} />
      {showLimitPopup && (
        <LimitPopup
          onSubscribe={onSubscribe}
          onClose={() => setShowLimitPopup(false)}
          remaining={entitlements.dailyQuestionsLimit}
        />
      )}
      {showSimuladosUpgrade && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[120] p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl animate-fade-in">
            <div className="text-center">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl">📝</span>
              </div>
              <h2 className="text-2xl font-black text-gray-900 mb-3">
                Meus Simulados é exclusivo do NOMEADO
              </h2>
              <p className="text-gray-600 mb-6">
                Tenha simulados estratégicos, histórico completo e desempenho
                detalhado.
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => {
                    setShowSimuladosUpgrade(false);
                    onSubscribe();
                  }}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-4 rounded-2xl transition shadow-lg shadow-emerald-500/20"
                >
                  Quero ser NOMEADO
                </button>
                <button
                  onClick={() => setShowSimuladosUpgrade(false)}
                  className="w-full text-gray-500 hover:text-gray-700 font-semibold py-2 transition"
                >
                  Agora não
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showAiLimitPopup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[120] p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl animate-fade-in">
            <div className="text-center">
              <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl">🎓</span>
              </div>
              <h2 className="text-2xl font-black text-gray-900 mb-3">
                Limite diário de explicações atingido
              </h2>
              <p className="text-gray-600 mb-6">
                Você já usou as 5 explicações do Professor Pobre hoje.
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => {
                    setShowAiLimitPopup(false);
                    onSubscribe();
                  }}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-4 rounded-2xl transition shadow-lg shadow-emerald-500/20"
                >
                  Quero ser NOMEADO
                </button>
                <button
                  onClick={() => setShowAiLimitPopup(false)}
                  className="w-full text-gray-500 hover:text-gray-700 font-semibold py-2 transition"
                >
                  Agora não
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmação para refazer plano */}
      {showResetPlanModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl animate-fade-in">
            <div className="text-center">
              <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl">🔄</span>
              </div>
              <h2 className="text-2xl font-black text-gray-900 mb-3">
                Refazer Plano de Estudos?
              </h2>
              <p className="text-gray-600 mb-6">
                Você vai refazer o diagnóstico e gerar um novo plano de estudos.
                <br />
                <br />
                <span className="text-emerald-600 font-semibold">
                  ✓ Seu progresso nas questões será mantido!
                </span>
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => {
                    setShowResetPlanModal(false);
                    onResetPlan();
                  }}
                  className="w-full bg-purple-500 hover:bg-purple-600 text-white font-bold py-4 rounded-2xl transition shadow-lg shadow-purple-500/20"
                >
                  Sim, refazer meu plano
                </button>
                <button
                  onClick={() => setShowResetPlanModal(false)}
                  className="w-full text-gray-500 hover:text-gray-700 font-semibold py-2 transition"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Styles for Highlight Mode */}
      {highlightMode && (
        <style>{`
          ::selection { background-color: #fde047; color: #000; }
          .question-text, .option-text { cursor: text; }
        `}</style>
      )}

      {/* Header - Hidden in Focus Mode */}
      {!focusMode && (
        <header className="bg-white border-b border-gray-100 shadow-sm px-4 py-4 sticky top-0 z-40 transition-all">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div
              className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition"
              onClick={onGoHome}
            >
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center font-black text-white text-lg shadow-md">
                CP
              </div>
              <div className="hidden md:block">
                <h1 className="font-bold text-gray-900">Concurseiro Pobre</h1>
                <p className="text-xs text-gray-500">
                  {entitlements.plan === "free"
                    ? `⭐ CONCURSEIRO • ${remainingQuestions}/${entitlements.dailyQuestionsLimit} questões hoje`
                    : entitlements.plan === "essencial"
                      ? `⭐ NOMEADO${planDaysRemaining !== null ? ` • vence em ${planDaysRemaining} dia(s)` : ""}`
                      : "👑 Premium"}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 md:gap-4 relative">
              <div className="hidden lg:block text-xs text-gray-500 font-semibold bg-gray-50 px-3 py-2 rounded-xl">
                Navegação principal no menu global acima ↑
              </div>

              {/* Stats (evita transbordo em telas menores) */}
              <div className="hidden xl:flex items-center gap-3 bg-gray-50 px-3 py-2 rounded-xl">
                <div className="text-center">
                  <p className="text-xs text-gray-500">Sequência</p>
                  <p className="font-bold text-emerald-600">{streak} 🔥</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-500">Acertos</p>
                  <p className="font-bold text-gray-900">{accuracy}%</p>
                </div>
              </div>

              {/* User menu (visível em desktop) */}
              <button
                onClick={onEditProfile}
                className="hidden md:flex items-center gap-2 hover:bg-gray-100 px-3 py-2 rounded-xl transition"
              >
                <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center overflow-hidden">
                  {userProfile.photoURL ? (
                    <img
                      src={userProfile.photoURL}
                      alt="Avatar"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-emerald-600 font-bold text-sm">
                      {userInitial}
                    </span>
                  )}
                </div>
                <span className="hidden md:block text-sm font-medium text-gray-700 max-w-[110px] truncate">
                  {userProfile.nickname ||
                    userProfile.displayName ||
                    user?.email?.split("@")[0] ||
                    "Usuário"}
                </span>
              </button>

              <button
                onClick={onLogout}
                className="hidden md:block text-gray-400 hover:text-red-500 transition p-2"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                  />
                </svg>
              </button>
            </div>
          </div>
        </header>
      )}


      {/* Checklist pós-login */}
      {!focusMode && (
        <div className="px-4 pt-3">
          <div className="max-w-7xl mx-auto bg-white border border-gray-100 rounded-2xl px-4 py-3 shadow-sm">
            <p className="text-xs font-black text-gray-500 uppercase tracking-wide mb-2">Primeiros passos</p>
            <div className="flex flex-wrap gap-2 text-xs">
              <span className={`px-3 py-1 rounded-full font-bold ${checklistDone.onboarding ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"}`}>1) Diagnóstico {checklistDone.onboarding ? "✓" : ""}</span>
              <span className={`px-3 py-1 rounded-full font-bold ${checklistDone.mapa ? "bg-indigo-100 text-indigo-700" : "bg-gray-100 text-gray-600"}`}>2) Mapa da Aprovação {checklistDone.mapa ? "✓" : ""}</span>
              <span className={`px-3 py-1 rounded-full font-bold ${checklistDone.questoes ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-600"}`}>3) Primeiras questões {checklistDone.questoes ? "✓" : ""}</span>
            </div>
          </div>
        </div>
      )}

      {/* Barra de progresso diário (plano gratuito) - Hidden in Focus Mode */}
      {entitlements.plan === "free" && !focusMode && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 transition-all">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-amber-600 text-sm font-medium">
                📊 Progresso diário: {questionsUsedToday}/
                {entitlements.dailyQuestionsLimit} questões
              </span>
            </div>
            <div className="w-32 h-2 bg-amber-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-amber-500 transition-all"
                style={{
                  width: `${(questionsUsedToday / entitlements.dailyQuestionsLimit) * 100}%`,
                }}
              />
            </div>
          </div>
        </div>
      )}

      <div
        className={`mx-auto px-4 py-6 transition-all duration-500 ${focusMode ? "max-w-6xl" : "max-w-screen-2xl 2xl:max-w-[1500px]"}`}
      >
        <div className="flex flex-col lg:flex-row lg:flex-wrap gap-6">
          {hasStarted && !focusMode && (
            <div className="w-full mb-2 lg:hidden">
              <button
                onClick={() => setFiltersCollapsed((v) => !v)}
                className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                {filtersCollapsed ? "🔎 Mostrar filtros" : "🧩 Ocultar filtros"}
              </button>
            </div>
          )}

          {/* Sidebar */}
          {hasStarted && (
            <div className="hidden lg:block w-80 flex-shrink-0 transition-all">
              <FilterPanel
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearFilters={() => {
                  setFilters(normalizeFilters());
                  setCurrentPage(1);
                }}
                availableSubjects={filterOptions.subjects}
                availableTopics={
                  filters.subject && filters.subject !== "Todas"
                    ? filterOptions.topics[filters.subject] || []
                    : []
                }
                availableBancas={filterOptions.bancas}
                availableYears={yearsForFilters}
                availableRoles={filterOptions.roles || []}
                totalQuestions={totalQuestions}
                loading={filterOptionsLoading || loadingQuestions}
              />
            </div>
          )}

          {/* Main content */}
          <div className="flex-1 min-w-0">
            {hasStarted && !filtersCollapsed && (
              <div className="lg:hidden mb-6">
                <FilterPanel
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  onClearFilters={() => {
                    setFilters(normalizeFilters());
                    setCurrentPage(1);
                  }}
                  availableSubjects={filterOptions.subjects}
                  availableTopics={
                    filters.subject && filters.subject !== "Todas"
                      ? filterOptions.topics[filters.subject] || []
                      : []
                  }
                  availableBancas={filterOptions.bancas}
                  availableYears={yearsForFilters}
                  availableRoles={filterOptions.roles || []}
                  totalQuestions={totalQuestions}
                  loading={filterOptionsLoading || loadingQuestions}
                />
              </div>
            )}

            {/* Tutoriais rápidos removidos temporariamente para evitar crash em produção */}

            {hasStarted && (
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <button
                  onClick={() => {
                    setHasStarted(false);
                    setFiltersCollapsed(false);
                  }}
                  className="px-3 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold"
                >
                  ← Voltar aos filtros
                </button>
                <button
                  onClick={onViewStudyPlan}
                  className="px-3 py-2 rounded-xl bg-purple-50 hover:bg-purple-100 text-purple-700 text-sm font-semibold"
                >
                  Ver plano
                </button>
                <button
                  onClick={onGoHome}
                  className="px-3 py-2 rounded-xl bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-sm font-semibold"
                >
                  Home
                </button>
              </div>
            )}

            {!hasStarted ? (
              <div className="max-w-screen-xl xl:max-w-screen-2xl mx-auto space-y-4">
                <div className="bg-white rounded-3xl p-4 shadow-lg border border-gray-200">
                  {lastFilterReminder && hasCustomFilters(lastFilterReminder) && (
                    <div className="mb-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-[11px] font-bold text-emerald-600 uppercase tracking-[0.1em]">Último filtro</p>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              const normalized = normalizeFilters(lastFilterReminder);
                              setFilters(normalized);
                              setHasStarted(true);
                              setCurrentPage(1);
                              setPageCursors({});
                            }}
                            className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-xl shadow-emerald-500/20 shadow"
                          >
                            Continuar
                          </button>
                          <button
                            onClick={() => {
                              setFilters(normalizeFilters());
                              setHasStarted(false);
                              setCurrentPage(1);
                              setPageCursors({});
                            }}
                            className="px-3 py-1.5 bg-white text-emerald-700 text-sm font-semibold rounded-xl border border-emerald-200 hover:bg-emerald-50"
                          >
                            Limpar
                          </button>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {lastFilterReminder.subject !== "Todas" && (
                          <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-50 text-emerald-800 border border-emerald-100 rounded-full text-xs font-semibold">
                            Matéria: {lastFilterReminder.subject}
                          </span>
                        )}
                        {lastFilterReminder.topic !== "Todos" && (
                          <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-50 text-emerald-800 border border-emerald-100 rounded-full text-xs font-semibold">
                            Tópico: {lastFilterReminder.topic}
                          </span>
                        )}
                        {lastFilterReminder.banca !== "Todas" && (
                          <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-50 text-emerald-800 border border-emerald-100 rounded-full text-xs font-semibold">
                            Banca: {lastFilterReminder.banca}
                          </span>
                        )}
                        {lastFilterReminder.institution !== "Todos" && (
                          <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-50 text-emerald-800 border border-emerald-100 rounded-full text-xs font-semibold">
                            Órgão: {lastFilterReminder.institution}
                          </span>
                        )}
                        {lastFilterReminder.role !== "Todos" && (
                          <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-50 text-emerald-800 border border-emerald-100 rounded-full text-xs font-semibold">
                            Cargo: {lastFilterReminder.role}
                          </span>
                        )}
                        {lastFilterReminder.year !== "Todos" && (
                          <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-50 text-emerald-800 border border-emerald-100 rounded-full text-xs font-semibold">
                            Ano: {lastFilterReminder.year}
                          </span>
                        )}
                        {lastFilterReminder.status !== "Todas" && (
                          <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-50 text-emerald-800 border border-emerald-100 rounded-full text-xs font-semibold">
                            Status: {statusLabels[lastFilterReminder.status] || lastFilterReminder.status}
                          </span>
                        )}
                        {lastFilterReminder.keyword && (
                          <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-50 text-emerald-800 border border-emerald-100 rounded-full text-xs font-semibold">
                            "{lastFilterReminder.keyword}"
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="bg-white rounded-2xl p-4 border border-gray-200 shadow-sm">
                    <FilterPanel
                      filters={filters}
                      onFilterChange={handleFilterChange}
                      onClearFilters={() => {
                        setFilters(normalizeFilters());
                        setCurrentPage(1);
                      }}
                      availableSubjects={filterOptions.subjects}
                      availableTopics={
                        filters.subject && filters.subject !== "Todas"
                          ? filterOptions.topics[filters.subject] || []
                          : []
                      }
                      availableBancas={filterOptions.bancas}
                      availableYears={yearsForFilters}
                      availableRoles={filterOptions.roles || []}
                      totalQuestions={totalQuestions}
                      loading={filterOptionsLoading || loadingQuestions}
                    />
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <button
                        onClick={() => setHasStarted(true)}
                        className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-3 rounded-2xl shadow-emerald-500/20 shadow"
                      >
                        Começar (Questões)
                      </button>
                      <button
                        onClick={() => {
                          const resolved = resolveFiltersFromPlanHelper(
                            filterOptions,
                            filters.subject,
                            filters.topic,
                          );
                          onViewFlashcards(resolved);
                          setHasStarted(true);
                        }}
                        className="w-full bg-amber-400 hover:bg-amber-500 text-amber-900 font-bold py-3 rounded-2xl border-2 border-amber-200 shadow-amber-200 shadow"
                      >
                        Estudar por Flashcards
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (loadingQuestions || filterOptionsLoading) ? (
              <div className="bg-white rounded-3xl p-6 md:p-8 shadow-lg border border-gray-200 relative overflow-hidden">
                <div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-emerald-50 to-transparent opacity-80 animate-[shimmer_1.6s_linear_infinite]"
                  aria-hidden="true"
                />
                <div className="relative space-y-6">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-2xl bg-emerald-100" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3 bg-gray-200 rounded-full w-28" />
                      <div className="h-4 bg-gray-100 rounded-full w-3/4" />
                    </div>
                    <div className="h-4 w-20 bg-gray-200 rounded-full" />
                  </div>
                  <div className="space-y-3">
                    {[...Array(5)].map((_, idx) => (
                      <div
                        key={idx}
                        className="h-3 bg-gray-100 rounded-full"
                        style={{ width: `${90 - idx * 10}%` }}
                      />
                    ))}
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    {[...Array(4)].map((_, idx) => (
                      <div
                        key={idx}
                        className="rounded-2xl border border-gray-100 p-4 bg-gray-50/70 space-y-2"
                      >
                        <div className="h-3 bg-gray-200 rounded-full w-24" />
                        <div className="h-4 bg-gray-100 rounded-full w-full" />
                        <div className="h-3 bg-gray-100 rounded-full w-3/4" />
                      </div>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {[...Array(3)].map((_, idx) => (
                      <span
                        key={idx}
                        className="h-8 px-4 rounded-full bg-gray-100 border border-gray-200 inline-flex items-center"
                        style={{ width: `${30 + idx * 10}%` }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            ) : currentQuestion ? (
              <>
                <div className="mb-4">
                  <div className="bg-white border border-gray-100 rounded-2xl px-4 py-3 shadow-sm flex items-center gap-3 text-sm">
                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 text-xl">🟢</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-emerald-600">Foco</p>
                      <div className="flex flex-wrap items-center gap-3 text-gray-800 font-semibold">
                        <span>Sequência: {streak} 🔥</span>
                        <span className="text-gray-300">|</span>
                        <span>Acertos: {accuracy}%</span>
                        <span className="text-gray-300">|</span>
                        <span>Respondidas: {totalAnswered}</span>
                        <span className="text-gray-300">|</span>
                        <span>Limite diário: {entitlements.plan === "free" ? Math.max(0, remainingQuestions) : "∞"}</span>
                      </div>
                    </div>
                    <span className="text-[10px] font-black uppercase tracking-[0.15em] text-gray-400">
                      {entitlements.plan === "free" ? "CONCURSEIRO" : "NOMEADO"}
                    </span>
                  </div>
                </div>

                <div className="bg-white rounded-3xl p-6 md:p-8 shadow-lg border border-gray-200">
                  {/* Question header */}
                  <div className="flex flex-wrap items-center gap-2 mb-6">
                    <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm font-semibold">
                      {currentQuestion.subject}
                    </span>
                    {currentQuestion.topic && (
                      <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-sm">
                        {currentQuestion.topic}
                      </span>
                    )}
                    {currentQuestion.banca && (
                      <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">
                        {currentQuestion.banca}
                      </span>
                    )}
                    {currentQuestion.year && (
                      <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm">
                        {currentQuestion.year}
                      </span>
                    )}
                  </div>

                  {/* Question text */}
                  <p
                    className={`text-gray-800 mb-8 transition-all ${getFontSizeClass("question")}`}
                  >
                    {currentQuestion.text}
                  </p>

                  {/* Options */}
                  <div className="space-y-3 mb-8">
                    {currentQuestion.options.map((option) => {
                      const isSelected = selectedOption === option.id;
                      const isCorrect =
                        option.id === currentQuestion.correctOptionId;
                      let optionClass =
                        "border-gray-200 hover:border-emerald-300 hover:bg-emerald-50";

                      if (showResult) {
                        if (isCorrect)
                          optionClass = "border-emerald-500 bg-emerald-50";
                        else if (isSelected)
                          optionClass = "border-red-500 bg-red-50";
                      } else if (isSelected) {
                        optionClass = "border-emerald-500 bg-emerald-50";
                      }

                      return (
                        <button
                          key={option.id}
                          onClick={() => handleOptionSelect(option.id)}
                          disabled={showResult || !canAnswer}
                          className={`w-full text-left p-4 rounded-xl border-2 transition-all ${optionClass} ${showResult || !canAnswer ? "cursor-default" : "cursor-pointer"}`}
                        >
                          <div className="flex items-start gap-3">
                            <span
                              className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 transition-all ${
                                showResult && isCorrect
                                  ? "bg-emerald-500 text-white"
                                  : showResult && isSelected
                                    ? "bg-red-500 text-white"
                                    : isSelected
                                      ? "bg-emerald-500 text-white"
                                      : "bg-gray-100 text-gray-600"
                              }`}
                            >
                              {option.id.toUpperCase()}
                            </span>
                            <span
                              className={`text-gray-700 pt-1 transition-all ${getFontSizeClass("option")}`}
                            >
                              {option.text}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    {!showResult ? (
                      <button
                        onClick={handleConfirm}
                        disabled={!selectedOption || !canAnswer}
                        className="w-full sm:flex-1 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 disabled:bg-gray-200 disabled:text-gray-400 text-white font-bold py-4 rounded-xl transition shadow-lg shadow-emerald-500/20"
                      >
                        {!canAnswer ? "Limite atingido" : "Confirmar Resposta"}
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={handleAskProfessor}
                          disabled={showExplanation}
                          className="w-full sm:flex-1 bg-gradient-to-r from-purple-500 to-fuchsia-500 hover:from-purple-600 hover:to-fuchsia-600 disabled:bg-purple-300 text-white font-bold py-4 rounded-xl transition flex items-center justify-center gap-2 shadow-lg shadow-purple-500/20"
                        >
                          <span>🤖</span> Perguntar ao Professor
                        </button>
                        <button
                          onClick={handleNext}
                          className="w-full sm:flex-1 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white font-bold py-4 rounded-xl transition shadow-lg shadow-teal-500/20"
                        >
                          Próxima Questão →
                        </button>
                      </>
                    )}
                  </div>

                  {/* Explanation */}
                  {showExplanation && (
                    <div className="mt-6 p-6 bg-purple-50 rounded-2xl border border-purple-200">
                      {loadingExplanation ? (
                        <div className="flex items-center gap-3">
                          <div className="animate-spin w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full"></div>
                          <span className="text-purple-700">
                            Professor Pobre está analisando...
                          </span>
                        </div>
                      ) : (
                        <>
                          <div className="prose prose-sm max-w-none text-gray-700">
                            <ReactMarkdown
                              rehypePlugins={[rehypeRaw, rehypeSanitize]}
                              components={{
                                p: (props) => <p className="mb-2 leading-relaxed" {...props} />,
                                strong: (props) => <strong className="font-semibold" {...props} />,
                                em: (props) => <em className="italic" {...props} />,
                                ul: (props) => <ul className="list-disc pl-5 mb-2" {...props} />,
                                ol: (props) => <ol className="list-decimal pl-5 mb-2" {...props} />,
                                li: (props) => <li className="mb-1" {...props} />,
                                br: () => <br />,
                              }}
                            >
                              {explanation}
                            </ReactMarkdown>
                          </div>
                          <div className="mt-4 flex flex-wrap gap-2">
                            <button
                              onClick={handleSaveExplanation}
                              disabled={savingExplanation}
                              className="px-4 py-2 bg-white text-purple-700 border border-purple-200 rounded-xl font-semibold hover:bg-purple-100 transition disabled:opacity-60"
                            >
                              {savingExplanation ? "Salvando..." : "Salvar Explicação"}
                            </button>
                            <button
                              onClick={onViewSavedExplanations}
                              className="px-4 py-2 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 transition"
                            >
                              Ver Caderno
                            </button>
                          </div>
                          {saveFeedback && (
                            <p className="mt-2 text-sm text-purple-700">{saveFeedback}</p>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="bg-white rounded-3xl p-12 shadow-xl border border-gray-200 text-center">
                <p className="text-gray-500">
                  Nenhuma questão encontrada com os filtros selecionados.
                </p>
              </div>
            )}

            {/* Pagination */}
            {totalQuestions > QUESTIONS_PER_PAGE && (
              <div className="mt-6">
                <Pagination
                  currentPage={currentPage}
                  totalPages={Math.ceil(totalQuestions / QUESTIONS_PER_PAGE)}
                  totalItems={totalQuestions}
                  itemsPerPage={QUESTIONS_PER_PAGE}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </div>

          {/* Question sidebar (desktop only para não sobrepor no mobile) */}
          {hasStarted && (
            <div className="hidden lg:block">
              <QuestionSidebar
                totalQuestions={totalQuestions}
                currentPage={currentPage}
                questionsPerPage={QUESTIONS_PER_PAGE}
                currentQuestionIndex={currentQuestionIndex}
                answeredQuestions={Object.entries(answeredQuestions).reduce(
                  (acc, [id, correct]) => ({
                    ...acc,
                    [id]: { correct },
                  }),
                  {},
                )}
                questions={questions}
                onQuestionSelect={(index) => {
                  setCurrentQuestionIndex(index);
                  setSelectedOption(null);
                  setShowResult(false);
                  setShowExplanation(false);
                }}
                onPageChange={setCurrentPage}
                isSubscribed={entitlements.plan !== "free"}
                freeQuestionsUsed={questionsUsedToday}
                freeQuestionsLimit={entitlements.dailyQuestionsLimit}
                totalAnswered={totalAnswered}
                totalCorrect={totalCorrect}
                onBookmark={handleBookmark}
                bookmarkedQuestions={bookmarkedQuestions}
                fontSize={fontSize}
                onFontSizeChange={setFontSize}
                focusMode={focusMode}
                onFocusModeToggle={setFocusMode}
                highlightMode={highlightMode}
                onHighlightToggle={setHighlightMode}
                onShowErrorNotebook={() => {
                  // Filtrar apenas erradas
                  handleFilterChange("status", "incorrect");
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== PERFORMANCE PAGE ====================

const PerformancePage: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-white">
      <header className="bg-white border-b border-gray-100 px-4 py-4 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <h1 className="text-2xl font-black text-gray-900">Meu Desempenho</h1>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 py-8">
        <UserStats />
      </main>
    </div>
  );
};

const RankingPage: React.FC<{
  onBack: () => void;
  entitlements: UserEntitlements;
}> = ({ onBack, entitlements }) => {
  const [period, setPeriod] = useState<"weekly" | "monthly" | "all">("weekly");
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [myEntry, setMyEntry] = useState<LeaderboardEntry | null>(null);
  const [isProUser, setIsProUser] = useState<boolean | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getLeaderboard(period);
        setEntries(data);
        const user = auth.currentUser;
        if (user) {
          const [mine, subscription, progress] = await Promise.all([
            getUserLeaderboardEntry(period, user.uid),
            getUserSubscription(),
            getUserProgress(),
          ]);
          setMyEntry(mine);
          setIsProUser(Boolean(subscription?.isPro || progress?.isPro));
        } else {
          setMyEntry(null);
          setIsProUser(false);
        }
      } catch (error: any) {
        console.error("Erro ao carregar ranking:", error);
        setError(error?.message || "Falha ao carregar ranking");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [period]);

  const participating = (isProUser ?? false) || entitlements.plan !== "free";
  const canShowParticipationBanner = !loading && isProUser !== null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-white">
      <header className="bg-white border-b border-gray-100 px-4 py-4 sticky top-0 z-40 shadow-sm">
        <div className="max-w-6xl mx-auto flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <div>
            <p className="text-xs uppercase text-gray-500 font-semibold tracking-wide">
              Comunidade CP
            </p>
            <h1 className="text-2xl font-black text-gray-900">Ranking</h1>
          </div>
          <div className="ml-auto flex items-center gap-2 bg-white border border-gray-100 rounded-2xl p-1 shadow-sm">
            {[{ key: "weekly", label: "Semanal" }, { key: "monthly", label: "Mensal" }, { key: "all", label: "Geral" }].map((p) => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key as any)}
                className={`px-3 py-1.5 rounded-xl text-sm font-bold transition ${period === p.key ? "bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-md shadow-emerald-500/20" : "text-gray-600 hover:bg-gray-100"}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 py-8 space-y-5">
        {canShowParticipationBanner && !participating && (
          <div className="bg-amber-50/80 border border-amber-200 text-amber-800 rounded-2xl p-4 text-sm font-semibold ">
            Participe do ranking tornando-se NOMEADO. Defina um apelido no seu
            perfil para aparecer aqui.
          </div>
        )}
        {canShowParticipationBanner && participating && (
          <div className="bg-emerald-50/80 border border-emerald-200 text-emerald-800 rounded-2xl p-4 text-sm font-semibold ">
            Você já é NOMEADO. Boa sorte no ranking! 🎉
          </div>
        )}
        {error && (
          <div className="bg-red-50/80 border border-red-200 text-red-700 rounded-2xl p-4 text-sm font-semibold ">
            Erro ao carregar ranking: {error}
          </div>
        )}
        {participating && myEntry && (
          <div className="bg-white rounded-2xl p-4 border border-emerald-100 shadow flex items-center justify-between text-sm font-semibold">
            <div className="flex items-center gap-3">
              <span className="text-emerald-600 font-black">Sua posição</span>
              <span className="px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 font-black">{(entries.findIndex((e) => e.userId === myEntry.userId) + 1) || 'Top 100+'}</span>
              <span className="px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 font-black">{myEntry.score} pts</span>
            </div>
            <div className="text-xs text-gray-500">{period === "weekly" ? "Semanal" : period === "monthly" ? "Mensal" : "Geral"}</div>
          </div>
        )}
        {loading ? (
          <div className="bg-white rounded-3xl p-6 border border-white/60 text-center shadow-lg">
            Carregando ranking...
          </div>
        ) : entries.length === 0 ? (
          <div className="bg-white rounded-3xl p-6 border border-white/60 text-center shadow-lg">
            Nenhum participante ainda.
          </div>
        ) : (
          <div className="space-y-5">
            {/* Podium top 3 */}
            <div className="grid md:grid-cols-3 gap-4">
              {entries.slice(0, 3).map((item, idx) => {
                const medals = ["🥇", "🥈", "🥉"];
                const gradients = [
                  "from-amber-400 via-orange-400 to-amber-500",
                  "from-gray-300 via-slate-300 to-gray-400",
                  "from-amber-200 via-orange-200 to-amber-300",
                ];
                return (
                  <div
                    key={item.userId}
                    className={`relative overflow-hidden rounded-3xl p-5 text-white shadow-2xl bg-gradient-to-br ${gradients[idx]}`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm text-white/80">
                          {medals[idx]}{" "}
                          {period === "weekly" ? "Semana" : "Mês"}{" "}
                        </p>
                        <h3 className="text-2xl font-black drop-shadow-sm flex items-center gap-2">
                          {item.nickname || "Concurseiro"}
                          {item.isPro && (
                            <span className="text-[10px] bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full font-black uppercase tracking-wide">NOMEADO</span>
                          )}
                        </h3>
                        <p className="text-sm text-white/80">
                          Pontuação {item.score}
                        </p>
                      </div>
                      <span className="px-3 py-1 rounded-full bg-white/20 text-xs font-bold">
                        #{idx + 1}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Lista completa */}
            <div className="bg-white rounded-3xl p-4 md:p-5 border border-white/60 shadow-xl">
              <div className="divide-y divide-gray-100/80">
                {entries.map((item, idx) => (
                  <div
                    key={item.userId}
                    className="flex items-center gap-4 py-3"
                  >
                    <div
                      className={`w-12 h-12 rounded-2xl flex items-center justify-center font-black text-lg shadow-md ${idx < 3 ? "bg-gradient-to-br from-emerald-500 to-teal-500 text-white" : "bg-gray-100 text-gray-800"}`}
                    >
                      #{idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-gray-900 truncate flex items-center gap-2">
                        {item.nickname || "Concurseiro"}
                        {item.isPro && (
                          <span className="text-[10px] bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full font-black uppercase tracking-wide">NOMEADO</span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500">
                        Pontuação: {item.score}
                      </p>
                    </div>
                    <div className="text-right text-sm font-semibold text-emerald-600">
                      {period === "weekly" ? "Semana" : "Mês"}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

// ==================== MAIN APP ====================

type AppView =
  | "landing"
  | "checkout"
  | "success"
  | "dashboard"
  | "profile"
  | "onboarding"
  | "studyPlan"
  | "admin"
  | "performance"
  | "simulados"
  | "ranking"
  | "guides"
  | "flashcards"
  | "savedExplanations"
  | "approvalMap"
  | "aulas";

type TenantModuleKey =
  | "dashboard"
  | "studyPlan"
  | "approvalMap"
  | "simulados"
  | "performance"
  | "ranking"
  | "flashcards"
  | "savedExplanations"
  | "materials"
  | "aulas"
  | "profile";

type TenantModules = Record<TenantModuleKey, boolean>;

type TenantBranding = {
  slug: string;
  name: string;
  headline: string;
  subheadline: string;
  accentClass: string;
  logoText?: string;
};

type TenantRouteContext = {
  slug: string;
  routeType: "login" | "app" | "root";
};

const DEFAULT_TENANT_MODULES: TenantModules = {
  dashboard: true,
  studyPlan: true,
  approvalMap: true,
  simulados: true,
  performance: true,
  ranking: true,
  flashcards: true,
  savedExplanations: true,
  materials: true,
  aulas: true,
  profile: true,
};

const mergeTenantModules = (raw: any): TenantModules => ({
  ...DEFAULT_TENANT_MODULES,
  ...(raw && typeof raw === "object" ? raw : {}),
});

const TENANT_DEMO_BRANDING: Record<string, TenantBranding> = {
  fabioramos: {
    slug: "fabioramos",
    name: "Professor Fábio Ramos",
    headline: "Área do Aluno • Turma Exclusiva",
    subheadline: "Entre com seu e-mail para acessar o ambiente de estudos institucional.",
    accentClass: "from-indigo-600 to-violet-600",
    logoText: "FR",
  },
};

const resolveTenantContextFromLocation = (): TenantRouteContext | null => {
  if (typeof window === "undefined") return null;
  const host = window.location.hostname.toLowerCase();
  const path = window.location.pathname.toLowerCase();

  const RESERVED_TENANT_SLUGS = new Set(["concurseiropobre", "www"]);

  const pathMatch = path.match(/^\/t\/([a-z0-9-]+)(?:\/(login|app))?/);
  if (pathMatch?.[1] && !RESERVED_TENANT_SLUGS.has(pathMatch[1])) {
    return {
      slug: pathMatch[1],
      routeType: (pathMatch[2] as "login" | "app" | undefined) || "root",
    };
  }

  // Tenant por subdomínio: somente quando for *.concurseiropobre.com.br
  // e nunca no domínio principal (concurseiropobre.com.br / www).
  const baseDomain = "concurseiropobre.com.br";
  if (host === baseDomain || host === `www.${baseDomain}`) return null;
  if (!host.endsWith(`.${baseDomain}`)) return null;

  const sub = host.slice(0, -(`.${baseDomain}`.length));
  if (!sub || RESERVED_TENANT_SLUGS.has(sub)) return null;
  return { slug: sub, routeType: "root" };
};

const App: React.FC = () => {
  // Safety net: nunca permitir rota de tenant reservada no domínio principal
  useEffect(() => {
    if (typeof window === "undefined") return;
    const host = window.location.hostname.toLowerCase();
    const path = window.location.pathname.toLowerCase();
    const isApex = host === "concurseiropobre.com.br" || host === "www.concurseiropobre.com.br";
    if (isApex && path.startsWith("/t/concurseiropobre")) {
      window.history.replaceState({}, "", "/");
    }
  }, []);

  const [redirectTarget, setRedirectTarget] = useState<
    "checkout" | "dashboard" | "onboarding" | "landing" | "flashcards" | "guides" | "onboarding-finish" | "approvalMap"
  >("dashboard");
  const [currentView, setCurrentView] = useState<AppView>("landing");
  const [tenantBranding, setTenantBranding] = useState<TenantBranding | null>(null);
  const [tenantModules, setTenantModules] = useState<TenantModules>(DEFAULT_TENANT_MODULES);
  const [tenantAccessDenied, setTenantAccessDenied] = useState<string | null>(null);
  const prevViewRef = useRef<AppView | null>(null);
  const currentViewRef = useRef<AppView>("landing");

  // Resolve tenant a cada render para definir o modo atual de rota
  const currentTenantRoute = typeof window !== "undefined" ? resolveTenantContextFromLocation() : null;
  const activeTenantContext = currentTenantRoute;

  useEffect(() => {
    // Sempre resolve do location atual para evitar herdar tenant de sessões anteriores
    const ctx = resolveTenantContextFromLocation();
    if (!ctx?.slug) {
      setTenantBranding(null);
      setTenantModules(DEFAULT_TENANT_MODULES);
      setTenantAccessDenied(null);
      return;
    }

    const fallback = TENANT_DEMO_BRANDING[ctx.slug] || {
      slug: ctx.slug,
      name: ctx.slug,
      headline: "Área do Aluno",
      subheadline: "Acesse com seu login.",
      accentClass: "from-slate-800 to-slate-900",
      logoText: ctx.slug.slice(0, 2).toUpperCase(),
    };
    setTenantBranding(fallback);
    setTenantModules(DEFAULT_TENANT_MODULES);

    (async () => {
      try {
        const remote = await getTenantBySlug(ctx.slug);
        if (!remote) return;
        if (remote.status && remote.status !== "active") {
          setTenantAccessDenied("Este ambiente está temporariamente inativo.");
          return;
        }
        setTenantModules(mergeTenantModules((remote as any).modules));
        setTenantBranding((prev) => ({
          slug: remote.slug || ctx.slug,
          name: remote.displayName || prev?.name || "Portal parceiro",
          headline: remote.branding?.loginTitle || prev?.headline || "Área do Aluno",
          subheadline:
            remote.branding?.loginSubtitle ||
            prev?.subheadline ||
            "Entre com sua conta para continuar.",
          accentClass: prev?.accentClass || "from-indigo-600 to-violet-600",
          logoText: prev?.logoText || (remote.displayName || "PT").slice(0, 2).toUpperCase(),
        }));
      } catch (error) {
        console.warn("Não foi possível carregar configuração remota do tenant", error);
      }
    })();
    // Nota: não depende de tenantContext para evitar loop; resolve do location atual a cada montagem
  }, [currentTenantRoute?.slug, currentTenantRoute?.routeType]);

  useEffect(() => {
    prevViewRef.current = currentViewRef.current;
    currentViewRef.current = currentView;
  }, [currentView]);



  const goBackToPrevious = (fallback: AppView = "dashboard") => {
    const previous = prevViewRef.current;
    if (!previous || previous === currentView) {
      setCurrentView(fallback);
      return;
    }
    setCurrentView(previous);
  };

  const [selectedPlan, setSelectedPlan] = useState<"essential" | "premium">(
    "essential",
  );
  const [user, setUser] = useState<User | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<"login" | "register">("register");
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState<UserProfile>({
    nickname: "",
    displayName: "",
    photoURL: "",
    targetExam: "",
    studyGoal: 20,
  });
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [onboardingData, setOnboardingData] = useState<OnboardingData | null>(
    null,
  );
  const [dashboardFilters, setDashboardFilters] = useState<QuestionFilters>({});
  const [flashcardFilters, setFlashcardFilters] = useState<QuestionFilters>({});
  const [showFlashcardsButton, setShowFlashcardsButton] = useState(false);
  const [aulasFilters, setAulasFilters] = useState<{ examId?: string | null; subject?: string | null; topic?: string | null }>({});
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    subjects: [],
    topics: {},
    bancas: [],
    institutions: [],
    years: [],
    roles: [],
  });
  const [purchasedItems, setPurchasedItems] = useState<string[]>([]);
  const [planExpiresAt, setPlanExpiresAt] = useState<Date | null>(null);
  const [tutorialList, setTutorialList] = useState(TUTORIALS);

  // Captura afiliado via querystring (?afiliado= / ?affiliate= / ?utm_campaign=) e persiste localmente
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const aff =
        params.get("afiliado") ||
        params.get("affiliate") ||
        params.get("utm_campaign") ||
        params.get("utm_source");
      if (aff) {
        localStorage.setItem("affiliateId", aff);
      }

      const coupon = params.get("cupom") || params.get("coupon");
      if (coupon) {
        localStorage.setItem("couponCode", coupon.toUpperCase());
      }
    } catch (err) {
      console.warn("Não foi possível capturar afiliado da URL", err);
    }
  }, []);

  // Carrega tutoriais do Firestore somente após login (landing usa seed estática)
  useEffect(() => {
    if (!user) {
      setTutorialList(TUTORIALS);
      return;
    }

    const loadTutorials = async () => {
      try {
        const q = query(collection(db, "tutorials"), orderBy("createdAt", "desc"), fbLimit(20));
        const snap = await getDocs(q);
        const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        if (items.length > 0) setTutorialList(items as any);
      } catch (err) {
        console.warn("Tutoriais fallback para seed", err);
        setTutorialList(TUTORIALS);
      }
    };
    loadTutorials();
  }, [user]);

  // Carrega filtros somente após login para reduzir custo no tráfego público
  useEffect(() => {
    if (!user) {
      setFilterOptions({
        subjects: [],
        topics: {},
        bancas: [],
        institutions: [],
        years: [],
        roles: [],
      });
      return;
    }

    const loadFilterOptions = async () => {
      try {
        const options = await getFilterOptions();

        // Deduplica Português / Língua Portuguesa e normaliza labels
        const SUBJECT_ALIASES: Record<string, string> = {
          "língua portuguesa": "Português",
          "lingua portuguesa": "Português",
          "portugues": "Português",
          "português": "Português",
        };
        const canon = (s: string = "") => {
          const key = normalizeLabel(s);
          return SUBJECT_ALIASES[key] || s;
        };

        const subjects = Array.from(
          new Map(
            (options.subjects || []).map((s) => {
              const c = canon(s);
              return [normalizeLabel(c), c];
            }),
          ).values(),
        );

        const topics: Record<string, string[]> = {};
        Object.entries(options.topics || {}).forEach(([subj, list]) => {
          const c = canon(subj);
          topics[c] = Array.from(new Set((list || []).map((t) => t.trim())));
        });

        setFilterOptions({
          ...options,
          subjects,
          topics,
        });
      } catch (error) {
        console.error("Error loading filter options:", error);
      }
    };
    loadFilterOptions();
  }, [user]);
  const [entitlements, setEntitlements] = useState<UserEntitlements>({
    plan: "free",
    dailyQuestionsLimit: 10,
    dailyQuestionsUsed: 0,
    aiExplanationLimit: 5,
    aiExplanationsUsed: 0,
    lastResetDate: new Date().toISOString().split("T")[0],
  });

  useEffect(() => {
    if (!activeTenantContext || !tenantBranding) return;

    const routeType = activeTenantContext.routeType;
    if (typeof window !== "undefined" && routeType === "root") {
      window.history.replaceState({}, "", `/t/${activeTenantContext.slug}/login`);
    }

    if (!user) {
      setTenantAccessDenied(null);
      return;
    }

    (async () => {
      try {
        const tenant = await getTenantBySlug(activeTenantContext.slug);
        const tenantId = tenant?.id || activeTenantContext.slug;
        const hasMembership = await isTenantMember(tenantId, user.uid);

        if (!hasMembership) {
          setTenantAccessDenied("Seu usuário não está vinculado a este tenant.");
          await auth.signOut();
          if (typeof window !== "undefined") {
            window.history.replaceState({}, "", `/t/${activeTenantContext.slug}/login`);
          }
          return;
        }

        setTenantAccessDenied(null);
        setCurrentView("dashboard");
        if (typeof window !== "undefined" && routeType !== "app") {
          window.history.replaceState({}, "", `/t/${activeTenantContext.slug}/app`);
        }
      } catch (error) {
        console.error("Erro ao validar acesso do tenant", error);
        setTenantAccessDenied("Não foi possível validar seu acesso. Tente novamente.");
        await auth.signOut();
      }
    })();
  }, [activeTenantContext?.slug, activeTenantContext?.routeType, tenantBranding, user]);

  const showToast = (type: Toast["type"], message: string) => {
    const id = Math.random().toString(36).substr(2, 9);
    setToasts((prev) => [...prev, { id, type, message }]);
    setTimeout(() => removeToast(id), 3000);
  };
  const removeToast = (id: string) =>
    setToasts((prev) => prev.filter((t) => t.id !== id));

  const refreshUserProgress = async () => {
    const data = await getUserProgress();
    if (data) {
      setPurchasedItems(data.purchasedItems || []);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        try {
          // Carrega entitlements e status de assinatura, e progresso do usuário (para perfil)
          const [userEntitlements, subscription, progressData] =
            await Promise.all([
              getUserEntitlements(),
              getUserSubscription(),
              getUserProgress(), // Busca os dados completos do UserProgress
            ]);

          // Sincroniza entitlements com o status real da assinatura
          if (subscription) {
            setEntitlements({
              ...userEntitlements,
              plan: subscription.isPro ? "essencial" : "free",
              dailyQuestionsUsed: subscription.freeQuestionsUsed,
              dailyQuestionsLimit: subscription.freeQuestionsLimit,
            });
          } else {
            setEntitlements(userEntitlements);
          }

          // Preenche o perfil do usuário com dados do Firestore
          if (progressData) {
            setUserProfile({
              nickname:
                progressData.nickname ||
                currentUser.displayName?.split(" ")[0] ||
                "",
              displayName: currentUser.displayName || "",
              photoURL: progressData.photoURL || currentUser.photoURL || "",
              targetExam: progressData.targetExam || "",
              studyGoal: progressData.weeklyGoal || 20, // Usa weeklyGoal como studyGoal para perfil
            });
            setPurchasedItems(progressData.purchasedItems || []);
            const exp = (progressData as any).expiresAt;
            setPlanExpiresAt(exp?.seconds ? new Date(exp.seconds * 1000) : null);
          } else {
            setPlanExpiresAt(null);
            setUserProfile((prev) => ({
              ...prev,
              displayName: currentUser.displayName || "",
              photoURL: currentUser.photoURL || "",
            }));
          }

          // Verifica se já completou onboarding
          const onboardingProgress = await getOnboardingProgress();
          if (onboardingProgress?.completed && onboardingProgress.data) {
            setOnboardingData(onboardingProgress.data);
          } else {
            if (onboardingData) {
              try {
                await completeOnboarding(onboardingData);
                setCurrentView("studyPlan");
              } catch (err) {
                console.error("Erro ao salvar onboarding pendente:", err);
                setCurrentView("onboarding");
              }
            } else {
              setCurrentView("onboarding");
            }
          }
        } catch (error) {
          console.error(error);
        }
      } else {
        setEntitlements({
          plan: "free",
          dailyQuestionsLimit: 10,
          dailyQuestionsUsed: 0,
          aiExplanationLimit: 5,
          aiExplanationsUsed: 0,
          lastResetDate: new Date().toISOString().split("T")[0],
        });
        setOnboardingData(null);
        setUserProfile({
          nickname: "",
          displayName: "",
          photoURL: "",
          targetExam: "",
          studyGoal: 20,
        });
        setPurchasedItems([]);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const handleStartCheckout = (
    planTier: "essential" | "premium" = "essential",
  ) => {
    const isPro = entitlements?.plan && entitlements.plan !== "free";

    if (isPro) {
      showToast(
        "info",
        "Você já é NOMEADO. Quer renovar ou mudar o ciclo? Pode seguir para o checkout.",
      );
    }

    setSelectedPlan(planTier);
    if (!user) {
      setRedirectTarget("checkout");
      setAuthMode("register");
      setShowAuthModal(true);
    } else {
      setCurrentView("checkout");
    }
  };

  const handleStartFreePlan = () => {
    // Permite iniciar o plano sem login; login será pedido para salvar ao final
    if (user) {
      if (onboardingData) {
        setCurrentView("studyPlan");
      } else {
        setCurrentView("onboarding");
      }
    } else {
      setCurrentView("onboarding");
    }
  };

  const ensureStudyPlanContext = async () => {
    try {
      const [progress, plan] = await Promise.all([
        getOnboardingProgress(),
        getStudyPlan(),
      ]);

      // Carrega onboarding localmente, se existir
      if (progress?.data) setOnboardingData(progress.data);

      // Se nunca completou onboarding, manda para onboarding
      if (!progress?.completed) {
        setCurrentView("onboarding");
        return false;
      }

      // Se onboarding está completo mas não há plano salvo, deixa StudyPlan gerar um novo
      if (!plan) {
        return true;
      }

      return true;
    } catch (error) {
      console.error("Erro ao garantir contexto do plano:", error);
      setCurrentView("onboarding");
      return false;
    }
  };

  const goToStudyPlan = async () => {
    const ok = await ensureStudyPlanContext();
    if (ok) setCurrentView("studyPlan");
  };

  const handleOnboardingComplete = async (data: OnboardingData) => {
    setOnboardingData(data);

    if (!user) {
      showToast("info", "Crie sua conta para salvar e acessar seu plano.");
      setRedirectTarget("onboarding-finish");
      setAuthMode("register");
      setShowAuthModal(true);
      return;
    }

    try {
      await completeOnboarding(data);
      showToast("success", "Diagnóstico completo! Gerando seu plano...");
    } catch (error) {
      console.error("Error saving onboarding:", error);
      showToast("error", "Erro ao salvar diagnóstico. Tente novamente.");
    }

    setCurrentView("studyPlan");
  };

  const handleOnboardingStepChange = async (
    step: number,
    data: Partial<OnboardingData>,
  ) => {
    // Persistência incremental
    try {
      await saveOnboardingProgress(step, data);
    } catch (error) {
      console.error("Error saving onboarding progress:", error);
    }
  };

  const handleAuthSuccess = async () => {
    setShowAuthModal(false);

    if (isTenantMode && activeTenantContext) {
      if (typeof window !== "undefined") {
        window.history.replaceState({}, "", `/t/${activeTenantContext.slug}/app`);
      }
      return;
    }

    // Se o alvo for landing, volta para a landing
    if (redirectTarget === "landing") {
      setCurrentView("landing");
      return;
    }

    // Se o alvo for checkout, vai direto
    if (redirectTarget === "checkout") {
      setCurrentView("checkout");
      return;
    }

    // Finalização do onboarding iniciado sem login
    if (redirectTarget === "onboarding-finish") {
      try {
        if (onboardingData) {
          await completeOnboarding(onboardingData);
          showToast("success", "Plano salvo! Gerando seu plano de estudos...");
          setCurrentView("studyPlan");
          return;
        }
        setCurrentView("onboarding");
        return;
      } catch (error) {
        console.error("Erro ao concluir onboarding após login:", error);
        showToast("error", "Não foi possível salvar seu plano. Tente novamente.");
        setCurrentView("onboarding");
        return;
      }
    }

    // Flashcards: vai direto para flashcards
    if (redirectTarget === "flashcards") {
      setCurrentView("flashcards");
      return;
    }

    if (redirectTarget === "guides") {
      setCurrentView("guides");
      return;
    }

    if (redirectTarget === "approvalMap") {
      setCurrentView("approvalMap");
      return;
    }

    // Dashboard não deve depender de onboarding completo
    try {
      const onboardingProgress = await getOnboardingProgress();
      if (onboardingProgress?.data) setOnboardingData(onboardingProgress.data);

      if (redirectTarget === "onboarding") {
        setCurrentView("onboarding");
        return;
      }

      setCurrentView("dashboard");
      if (!onboardingProgress?.completed) {
        showToast("info", "Você pode completar o diagnóstico depois, sem bloquear o acesso às questões.");
      }
    } catch (error) {
      console.error("Erro ao verificar onboarding:", error);
      setCurrentView("dashboard"); // fallback seguro sem bloqueio
    }
  };

  const isTenantMode = !!currentTenantRoute?.slug;
  const showTenantLoginScreen = isTenantMode && !user;

  const isModuleEnabled = (key: TenantModuleKey) => !isTenantMode || !!tenantModules[key];

  const getFirstEnabledTenantView = (): AppView => {
    const ordered: Array<{ key: TenantModuleKey; view: AppView }> = [
      { key: "dashboard", view: "dashboard" },
      { key: "studyPlan", view: "studyPlan" },
      { key: "approvalMap", view: "approvalMap" },
      { key: "simulados", view: "simulados" },
      { key: "performance", view: "performance" },
      { key: "ranking", view: "ranking" },
      { key: "flashcards", view: "flashcards" },
      { key: "savedExplanations", view: "savedExplanations" },
      { key: "materials", view: "dashboard" },
      { key: "aulas", view: "aulas" },
      { key: "profile", view: "profile" },
    ];
    const found = ordered.find((item) => !!tenantModules[item.key]);
    return found?.view || "dashboard";
  };

  useEffect(() => {
    if (!isTenantMode || !user) return;
    const viewMap: Partial<Record<string, TenantModuleKey>> = {
      dashboard: "dashboard",
      studyPlan: "studyPlan",
      approvalMap: "approvalMap",
      simulados: "simulados",
      performance: "performance",
      ranking: "ranking",
      flashcards: "flashcards",
      savedExplanations: "savedExplanations",
      materials: "materials",
      aulas: "aulas",
      profile: "profile",
    };
    const moduleKey = viewMap[currentView as string];
    if (moduleKey && !tenantModules[moduleKey]) {
      setCurrentView(getFirstEnabledTenantView());
    }
  }, [currentView, isTenantMode, user, tenantModules]);

  // ==================== RENDERIZAÇÃO ====================

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const showLoggedNav =
    !!user &&
    ["dashboard", "studyPlan", "approvalMap", "simulados", "performance", "ranking", "materials", "flashcards", "savedExplanations", "aulas", "profile"].includes(currentView);

  const renderTenantLoginShell = () => (
    <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" />
      {tenantBranding && (
        <div className={`absolute inset-0 bg-gradient-to-br ${tenantBranding.accentClass} opacity-20`} />
      )}
      <div className="relative z-10 w-full max-w-md bg-white/10 border border-white/20 backdrop-blur-xl rounded-3xl p-8 shadow-2xl">
        {tenantBranding && (
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-white text-slate-900 font-black flex items-center justify-center">
              {tenantBranding.logoText || "CP"}
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.12em] text-white/70">Portal da empresa</p>
              <h1 className="text-lg font-black">{tenantBranding.name}</h1>
            </div>
          </div>
        )}

        <h2 className="text-2xl font-black mb-2">{tenantBranding?.headline || "Área do Aluno"}</h2>
        <p className="text-white/80 text-sm mb-8">{tenantBranding?.subheadline || "Acesse com seu login."}</p>

        <button
          onClick={() => {
            setTenantAccessDenied(null);
            setRedirectTarget("dashboard");
            setAuthMode("login");
            setShowAuthModal(true);
            if (typeof window !== "undefined" && activeTenantContext?.slug) {
              window.history.replaceState({}, "", `/t/${activeTenantContext.slug}/login`);
            }
          }}
          className="w-full bg-white text-slate-900 hover:bg-slate-100 font-black py-4 rounded-2xl transition"
        >
          Entrar na Área do Aluno
        </button>
        {tenantAccessDenied && (
          <p className="mt-3 text-xs text-red-200 text-center bg-red-500/20 border border-red-300/30 rounded-xl p-2">
            {tenantAccessDenied}
          </p>
        )}
        <p className="mt-3 text-xs text-white/60 text-center">Acesso exclusivo para alunos vinculados ao tenant.</p>
      </div>
    </div>
  );

  if (showTenantLoginScreen) {
    return (
      <AppContext.Provider value={{ toasts, showToast, removeToast }}>
        <style>{`
          @keyframes slide-in { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } } 
          @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } } 
          @keyframes confetti { 0% { transform: translateY(-10vh) rotate(0deg); opacity: 1; } 100% { transform: translateY(110vh) rotate(720deg); opacity: 0; } } 
          @keyframes shimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }
          .animate-slide-in { animation: slide-in 0.3s ease-out; } 
          .animate-fade-in { animation: fade-in 0.3s ease-out; } 
          .animate-confetti { animation: confetti 2s ease-out forwards; }
        `}</style>
        <ToastContainer />

        {showAuthModal && (
          <AuthModal
            mode={authMode}
            onClose={() => setShowAuthModal(false)}
            onSuccess={handleAuthSuccess}
            onSwitchMode={() =>
              setAuthMode(authMode === "login" ? "register" : "login")
            }
          />
        )}

        {renderTenantLoginShell()}
      </AppContext.Provider>
    );
  }

  return (
    <AppContext.Provider value={{ toasts, showToast, removeToast }}>
      <style>{`
        @keyframes slide-in { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } } 
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } } 
        @keyframes confetti { 0% { transform: translateY(-10vh) rotate(0deg); opacity: 1; } 100% { transform: translateY(110vh) rotate(720deg); opacity: 0; } } 
        @keyframes shimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }
        .animate-slide-in { animation: slide-in 0.3s ease-out; } 
        .animate-fade-in { animation: fade-in 0.3s ease-out; } 
        .animate-confetti { animation: confetti 2s ease-out forwards; }
      `}</style>
      <ToastContainer />

      {showAuthModal && (
        <AuthModal
          mode={authMode}
          onClose={() => setShowAuthModal(false)}
          onSuccess={handleAuthSuccess}
          onSwitchMode={() =>
            setAuthMode(authMode === "login" ? "register" : "login")
          }
        />
      )}

      {!showTenantLoginScreen && showLoggedNav && (
        <div className="sticky top-0 z-[70] bg-white/95 backdrop-blur border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 py-2 flex items-center gap-2 overflow-x-auto text-xs">
            {isModuleEnabled("dashboard") && <button onClick={() => setCurrentView("dashboard")} className={`px-3 py-2 rounded-lg font-bold whitespace-nowrap ${currentView === "dashboard" ? "bg-emerald-500 text-white" : "bg-gray-100 text-gray-700"}`}>Área do Aluno</button>}
            {isModuleEnabled("approvalMap") && <button onClick={() => setCurrentView("approvalMap")} className={`px-3 py-2 rounded-lg font-bold whitespace-nowrap ${currentView === "approvalMap" ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-700"}`}>Mapa da Aprovação</button>}
            {isModuleEnabled("studyPlan") && <button onClick={() => { void goToStudyPlan(); }} className={`px-3 py-2 rounded-lg font-semibold whitespace-nowrap ${currentView === "studyPlan" ? "bg-purple-500 text-white" : "bg-gray-100 text-gray-700"}`}>Plano</button>}
            {isModuleEnabled("simulados") && <button onClick={() => setCurrentView("simulados")} className={`px-3 py-2 rounded-lg font-semibold whitespace-nowrap ${currentView === "simulados" ? "bg-orange-500 text-white" : "bg-gray-100 text-gray-700"}`}>Simulados</button>}
            {isModuleEnabled("performance") && <button onClick={() => setCurrentView("performance")} className={`px-3 py-2 rounded-lg font-semibold whitespace-nowrap ${currentView === "performance" ? "bg-blue-500 text-white" : "bg-gray-100 text-gray-700"}`}>Desempenho</button>}
            {isModuleEnabled("ranking") && <button onClick={() => setCurrentView("ranking")} className={`px-3 py-2 rounded-lg font-semibold whitespace-nowrap ${currentView === "ranking" ? "bg-yellow-500 text-white" : "bg-gray-100 text-gray-700"}`}>Ranking</button>}
            {isModuleEnabled("flashcards") && <button onClick={() => setCurrentView("flashcards")} className={`px-3 py-2 rounded-lg font-semibold whitespace-nowrap ${currentView === "flashcards" ? "bg-amber-500 text-white" : "bg-gray-100 text-gray-700"}`}>Flashcards</button>}
            {isModuleEnabled("savedExplanations") && <button onClick={() => setCurrentView("savedExplanations")} className={`px-3 py-2 rounded-lg font-semibold whitespace-nowrap ${currentView === "savedExplanations" ? "bg-teal-600 text-white" : "bg-gray-100 text-gray-700"}`}>Explicações</button>}
            {isModuleEnabled("materials") && <button onClick={() => setCurrentView("materials")} className={`px-3 py-2 rounded-lg font-semibold whitespace-nowrap ${currentView === "materials" ? "bg-emerald-700 text-white" : "bg-gray-100 text-gray-700"}`}>Materiais</button>}
            {isModuleEnabled("aulas") && <button onClick={() => { setAulasFilters({}); setCurrentView("aulas"); }} className={`px-3 py-2 rounded-lg font-semibold whitespace-nowrap ${currentView === "aulas" ? "bg-cyan-700 text-white" : "bg-gray-100 text-gray-700"}`}>Aulas</button>}
            {isModuleEnabled("profile") && <button onClick={() => setCurrentView("profile")} className={`px-3 py-2 rounded-lg font-semibold whitespace-nowrap ${currentView === "profile" ? "bg-slate-700 text-white" : "bg-gray-100 text-gray-700"}`}>Perfil</button>}
          </div>
        </div>
      )}

      {!showTenantLoginScreen && (currentView === "checkout" ? (
        <CheckoutPage
          onBack={() => goBackToPrevious("landing")}
          selectedPlan={selectedPlan}
          onSuccess={async () => {
            if (user) {
              try {
                setCurrentView("success");
              } catch (error) {
                console.error("Erro ao salvar assinatura:", error);
                showToast(
                  "error",
                  "Erro ao confirmar assinatura. Contate o suporte.",
                );
              }
            }
          }}
        />
      ) : currentView === "success" ? (
        <SuccessPage onContinue={() => setCurrentView("dashboard")} />
      ) : currentView === "admin" ? (
        <Suspense fallback={<div className="p-6 text-center text-gray-600">Carregando painel admin...</div>}>
          <AdminPage onBack={() => goBackToPrevious("dashboard")} />
        </Suspense>
      ) : currentView === "simulados" ? (
        <SimuladosPage
          onBack={() => goBackToPrevious("dashboard")}
          onStartSimulado={(filters) => {
            setDashboardFilters(filters);
            setCurrentView("dashboard");
          }}
          filterOptions={filterOptions} // Passando as filterOptions reais
        />
      ) : currentView === "performance" ? (
        <PerformancePage onBack={() => goBackToPrevious("dashboard")} />
      ) : currentView === "ranking" ? (
        <RankingPage
          onBack={() => goBackToPrevious("dashboard")}
          entitlements={entitlements}
        />
      ) : currentView === "profile" ? (
        <ProfilePage
          user={user}
          onBack={() => goBackToPrevious("dashboard")}
          userProfile={userProfile}
          onSave={async (p) => {
            setUserProfile(p);
            if (user) {
              await updateUserProfile(p.nickname, p.targetExam, p.photoURL);
            }
          }}
          showToast={showToast}
          onboardingData={onboardingData}
        />
      ) : currentView === "onboarding" ? (
        <OnboardingWizard
          onComplete={handleOnboardingComplete}
          onBack={() => goBackToPrevious("landing")}
          onSkip={() => {
            showToast("info", "Diagnóstico pulado por enquanto. Você pode concluir depois.");
            setCurrentView("dashboard");
          }}
          initialData={onboardingData || undefined}
          onStepChange={handleOnboardingStepChange}
        />
      ) : currentView === "studyPlan" && onboardingData ? (
        <StudyPlanDisplay
          user={user}
          onboardingData={onboardingData}
          onStartStudying={() => {
            console.log("Navegando para dashboard...");
            setCurrentView("dashboard");
          }}
          onBack={() => goBackToPrevious("dashboard")}
          onViewQuestions={(subject, topic) => {
            const resolved = resolveFiltersFromPlanHelper(filterOptions, subject, topic);
            setDashboardFilters(resolved);
            setCurrentView("dashboard");
          }}
          onViewFlashcards={(subject, topic) => {
            const resolved = resolveFiltersFromPlanHelper(filterOptions, subject, topic);
            setFlashcardFilters(resolved);
            setCurrentView("flashcards");
          }}
          onResetPlan={async () => {
            try {
              await resetOnboarding();
              setOnboardingData(null);
              setCurrentView("onboarding");
              showToast("info", "Vamos refazer seu plano de estudos!");
            } catch (error) {
              console.error("Erro ao resetar plano:", error);
              showToast("error", "Erro ao resetar plano. Tente novamente.");
            }
          }}
        />
      ) : currentView === "dashboard" ? (
        <Dashboard
          user={user}
          onLogout={() => {
            auth.signOut();
            setCurrentView("landing");
            if (isTenantMode && activeTenantContext && typeof window !== "undefined") {
              window.history.replaceState({}, "", `/t/${activeTenantContext.slug}/login`);
            }
          }}
          entitlements={entitlements}
          onSubscribe={() => handleStartCheckout("essential")}
          showToast={showToast}
          userProfile={userProfile}
          onEditProfile={() => setCurrentView("profile")}
          onViewPerformance={() => setCurrentView("performance")}
          onViewSimulados={() => setCurrentView("simulados")} // Passando a nova função
          onViewRanking={() => setCurrentView("ranking")}
          onboardingData={onboardingData}
          initialFilters={dashboardFilters}
          onViewStudyPlan={goToStudyPlan}
          onViewApprovalMap={() => setCurrentView("approvalMap")}
          onViewMaterials={() => setCurrentView("materials")}
          onGoHome={() => setCurrentView("landing")}
          onViewFlashcards={(filters) => {
            setFlashcardFilters(filters);
            setCurrentView("flashcards");
          }}
          onViewSavedExplanations={() => setCurrentView("savedExplanations")}
          onResetPlan={async () => {
            try {
              await resetOnboarding();
              setOnboardingData(null);
              setCurrentView("onboarding");
              showToast("info", "Vamos refazer seu plano de estudos!");
            } catch (error) {
              console.error("Erro ao resetar plano:", error);
              showToast("error", "Erro ao resetar plano. Tente novamente.");
            }
          }}
          planExpiresAt={planExpiresAt}
        />
      ) : currentView === "approvalMap" ? (
        <ApprovalMapPage
          onboardingData={onboardingData}
          onBack={() => goBackToPrevious("dashboard")}
          onGoDashboard={() => setCurrentView("dashboard")}
          onCreatePlan={() => setCurrentView("onboarding")}
          onViewAulas={(subject, topic) => {
            setAulasFilters({ examId: "pmerj-2026", subject, topic });
            setCurrentView("aulas");
          }}
        />
      ) : currentView === "guides" ? (
        <GuidesPage
          onBack={() => goBackToPrevious("landing")}
          purchasedItems={purchasedItems}
          isLoggedIn={!!user}
          onLogin={() => {
            setRedirectTarget("guides");
            setAuthMode("login");
            setShowAuthModal(true);
          }}
        />
      ) : currentView === "materials" ? (
        <MaterialsPage
          onBack={() => goBackToPrevious("dashboard")}
          purchasedItems={purchasedItems}
          refreshUser={refreshUserProgress}
          showToast={showToast}
        />
      ) : currentView === "aulas" ? (
        <AulasPage
          onBack={() => goBackToPrevious("dashboard")}
          initialExamId={aulasFilters.examId || null}
          initialSubject={aulasFilters.subject || null}
          initialTopic={aulasFilters.topic || null}
        />
      ) : currentView === "flashcards" ? (
        <FlashcardsPage
          filters={flashcardFilters}
          onBack={() => goBackToPrevious("dashboard")}
          onViewSavedExplanations={() => setCurrentView("savedExplanations")}
        />
      ) : currentView === "savedExplanations" ? (
        <SavedExplanationsPage onBack={() => goBackToPrevious("dashboard")} />
      ) : (
        <>
          {isTenantMode && tenantBranding && !user && currentView === "landing" && (
            <div className={`mx-auto mt-4 mb-2 max-w-5xl rounded-2xl bg-gradient-to-r ${tenantBranding.accentClass} p-4 text-white shadow-lg`}>
              <p className="text-xs uppercase tracking-wide opacity-90">Subdomínio parceiro (demo)</p>
              <h2 className="text-xl font-black">{tenantBranding.name}</h2>
              <p className="text-sm opacity-95">{tenantBranding.headline}</p>
              <p className="text-sm opacity-90">{tenantBranding.subheadline}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  onClick={() => {
                    setRedirectTarget("dashboard");
                    setAuthMode("login");
                    setShowAuthModal(true);
                  }}
                  className="rounded-lg bg-white px-4 py-2 text-sm font-bold text-gray-900"
                >
                  Entrar como aluno da turma
                </button>
                <span className="rounded-lg border border-white/40 px-3 py-2 text-xs">
                  Tenant: {tenantBranding.slug}
                </span>
              </div>
            </div>
          )}
          <NewLandingPage
            onStartCheckout={handleStartCheckout}
            onStartFreePlan={handleStartFreePlan}
            onLogin={() => {
              setRedirectTarget(tenantBranding ? "dashboard" : "landing");
              setAuthMode("login");
              setShowAuthModal(true);
            }}
            isLoggedIn={!!user}
            hasStudyPlan={!!onboardingData}
            onGoToDashboard={async () => {
              if (user) {
                try {
                  const progress = await getOnboardingProgress();
                  if (progress?.data) setOnboardingData(progress.data);
                  if (!progress?.completed) {
                    showToast("info", "Você pode completar o diagnóstico depois, sem bloquear o acesso às questões.");
                  }
                } catch (error) {
                  console.error("Erro ao carregar progresso de onboarding:", error);
                }
                setCurrentView("dashboard");
              } else {
                setRedirectTarget("dashboard");
                setAuthMode("login");
                setShowAuthModal(true);
              }
            }}
          onGoToApprovalMap={() => {
            if (user) {
              setCurrentView("approvalMap");
            } else {
              setRedirectTarget("approvalMap");
              setAuthMode("login");
              setShowAuthModal(true);
            }
          }}
          onViewGuides={() => setCurrentView("guides")}
          onStartFlashcards={() => {
            if (user) {
              setFlashcardFilters({});
              setCurrentView("flashcards");
            } else {
              setRedirectTarget("flashcards");
              setAuthMode("login");
              setShowAuthModal(true);
            }
          }}
        />
        </>
      ))}
      {/* Admin Access Button */}
      {user &&
        (user.email === "admin@concurseiropobre.com.br" ||
          user.email === "felipe@example.com") && (
          <button
            onClick={() => setCurrentView("admin")}
            className="fixed bottom-4 left-4 z-[200] bg-gray-900 text-white px-4 py-2 rounded-full text-xs font-bold shadow-lg opacity-50 hover:opacity-100 transition"
          >
            🕵️ Admin
          </button>
        )}
    </AppContext.Provider>
  );
};

export default App;



